import { getCurrentHub, addBreadcrumb, captureException, addGlobalEventProcessor, setContext } from '@sentry/core';
import { htmlTreeAsString, logger as logger$1, uuid4, browserPerformanceTimeOrigin, addInstrumentationHandler, createEnvelope } from '@sentry/utils';
import debounce from 'lodash.debounce';
import { record, EventType } from 'rrweb';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

function createBreadcrumb(breadcrumb) {
    return __assign({ timestamp: new Date().getTime() / 1000, type: 'default' }, breadcrumb);
}

function handleDom(handlerData) {
    // Taken from https://github.com/getsentry/sentry-javascript/blob/master/packages/browser/src/integrations/breadcrumbs.ts#L112
    var target;
    var targetNode;
    // Accessing event.target can throw (see getsentry/raven-js#838, #768)
    try {
        targetNode =
            handlerData.event.target ||
                handlerData.event;
        target = htmlTreeAsString(targetNode);
    }
    catch (e) {
        target = '<unknown>';
    }
    if (target.length === 0) {
        return null;
    }
    return createBreadcrumb({
        category: "ui.".concat(handlerData.name),
        message: target,
        data: __assign({}, (targetNode ? { nodeId: record.mirror.getId(targetNode) } : {})),
    });
}

var _LAST_BREADCRUMB = null;
function handleScope(scope) {
    var _a;
    //@ts-expect-error using private val
    var newBreadcrumb = scope._breadcrumbs[scope._breadcrumbs.length - 1];
    // Listener can be called when breadcrumbs have not changed, so we store the
    // reference to the last crumb and only return a crumb if it has changed
    if (_LAST_BREADCRUMB === newBreadcrumb || !newBreadcrumb) {
        return null;
    }
    _LAST_BREADCRUMB = newBreadcrumb;
    if (['fetch', 'xhr', 'sentry.event', 'sentry.transaction'].includes(newBreadcrumb.category) ||
        ((_a = newBreadcrumb.category) === null || _a === void 0 ? void 0 : _a.startsWith('ui.'))) {
        return null;
    }
    return createBreadcrumb(newBreadcrumb);
}

function getBreadcrumbHandler(type) {
    if (type === 'scope') {
        return handleScope;
    }
    return handleDom;
}

var isBrowser$1 = typeof window !== 'undefined';
/**
 * Returns true if we are currently recording an internal to Sentry replay
 * (e.g. on https://sentry.io )
 */
function isInternal() {
    return (isBrowser$1 &&
        ['sentry.io', 'dev.getsentry.net'].includes(window.location.host));
}

/**
 * Checks is `targetHost` is a Sentry ingestion host
 */
function isIngestHost(targetHost) {
    var _a;
    var _b = ((_a = getCurrentHub().getClient()) === null || _a === void 0 ? void 0 : _a.getDsn()) || {}, protocol = _b.protocol, host = _b.host;
    // XXX: Special case when this integration is used by Sentry on `sentry.io`
    // We would like to capture network requests made to our ingest endpoints for debugging
    if (isInternal()) {
        return false;
    }
    return targetHost.startsWith("".concat(protocol, "://").concat(host));
}

function handleFetch(handlerData) {
    if (!handlerData.endTimestamp) {
        return null;
    }
    var startTimestamp = handlerData.startTimestamp, endTimestamp = handlerData.endTimestamp, fetchData = handlerData.fetchData, response = handlerData.response;
    // Do not capture fetches to Sentry ingestion endpoint
    if (isIngestHost(fetchData.url)) {
        return null;
    }
    return {
        type: 'resource.fetch',
        start: startTimestamp / 1000,
        end: endTimestamp / 1000,
        name: fetchData.url,
        data: {
            method: fetchData.method,
            statusCode: response.status,
        },
    };
}

function handleHistory(handlerData) {
    var from = handlerData.from, to = handlerData.to;
    var now = new Date().getTime() / 1000;
    return {
        type: 'navigation.push',
        start: now,
        end: now,
        name: to,
        data: {
            previous: from,
        },
    };
}

function handleXhr(handlerData) {
    var _a;
    if (handlerData.xhr.__sentry_own_request__) {
        // Taken from sentry-javascript
        // Only capture non-sentry requests
        return null;
    }
    if (handlerData.startTimestamp) {
        // TODO: See if this is still needed
        handlerData.xhr.__sentry_xhr__ = handlerData.xhr.__sentry_xhr__ || {};
        handlerData.xhr.__sentry_xhr__.startTimestamp = handlerData.startTimestamp;
    }
    if (!handlerData.endTimestamp) {
        return null;
    }
    var _b = handlerData.xhr.__sentry_xhr__ || {}, method = _b.method, url = _b.url, statusCode = _b.status_code;
    // Do not capture fetches to Sentry ingestion endpoint
    if (url === undefined || isIngestHost(url)) {
        return null;
    }
    return {
        type: 'resource.xhr',
        name: url,
        start: (((_a = handlerData.xhr.__sentry_xhr__) === null || _a === void 0 ? void 0 : _a.startTimestamp) || 0) / 1000 ||
            handlerData.endTimestamp / 1000.0,
        end: handlerData.endTimestamp / 1000.0,
        data: {
            method: method,
            statusCode: statusCode,
        },
    };
}

function getSpanHandler(type) {
    if (type === 'fetch') {
        return handleFetch;
    }
    if (type === 'xhr') {
        return handleXhr;
    }
    return handleHistory;
}

var REPLAY_SESSION_KEY = 'sentryReplaySession';
var REPLAY_EVENT_NAME = 'replay_event';
// The idle limit for a session
var SESSION_IDLE_DURATION = 300000; // 5 minutes in ms
// Grace period to keep a session when a user changes tabs or hides window
var VISIBILITY_CHANGE_TIMEOUT = SESSION_IDLE_DURATION;
// The maximum length of a session
var MAX_SESSION_LIFE = 1800000; // 30 minutes
/**
 * Defaults for sampling rates
 */
var DEFAULT_SESSION_SAMPLE_RATE = 0.1;
var DEFAULT_ERROR_SAMPLE_RATE = 1.0;

/**
 * Deletes a session from storage
 */
function deleteSession() {
    var hasSessionStorage = 'sessionStorage' in window;
    if (!hasSessionStorage) {
        return;
    }
    try {
        window.sessionStorage.removeItem(REPLAY_SESSION_KEY);
    }
    catch (_a) {
        // Ignore potential SecurityError exceptions
    }
}

/**
 * Given an initial timestamp and an expiry duration, checks to see if current
 * time should be considered as expired.
 */
function isExpired(initialTime, expiry, targetTime) {
    if (targetTime === void 0) { targetTime = +new Date(); }
    // Always expired if < 0
    if (initialTime === null || expiry === undefined || expiry < 0) {
        return true;
    }
    // Never expires if == 0
    if (expiry === 0) {
        return false;
    }
    return initialTime + expiry <= targetTime;
}

/**
 * Checks to see if session is expired
 */
function isSessionExpired(session, idleTimeout, targetTime) {
    if (targetTime === void 0) { targetTime = +new Date(); }
    return (
    // First, check that maximum session length has not been exceeded
    isExpired(session.started, MAX_SESSION_LIFE, targetTime) ||
        // check that the idle timeout has not been exceeded (i.e. user has
        // performed an action within the last `idleTimeout` ms)
        isExpired(session === null || session === void 0 ? void 0 : session.lastActivity, idleTimeout, targetTime));
}

function wrapLogger(logFn) {
    return function wrappedLog() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return logFn.call.apply(logFn, __spreadArray([logger$1, '[Replay]'], args, false));
    };
}
var logger = __assign(__assign({}, logger$1), { error: wrapLogger(logger$1.error), warn: wrapLogger(logger$1.warn), log: wrapLogger(logger$1.log) });

function saveSession(session) {
    var hasSessionStorage = 'sessionStorage' in window;
    if (!hasSessionStorage) {
        return;
    }
    try {
        window.sessionStorage.setItem(REPLAY_SESSION_KEY, JSON.stringify(session));
    }
    catch (_a) {
        // Ignore potential SecurityError exceptions
    }
}

/**
 * Given a sample rate, returns true if replay should be sampled.
 *
 * 1.0 = 100% sampling
 * 0.0 = 0% sampling
 */
function isSampled(sampleRate) {
    if (sampleRate === undefined) {
        return false;
    }
    // Math.random() returns a number in range of 0 to 1 (inclusive of 0, but not 1)
    return Math.random() < sampleRate;
}

var Session = /** @class */ (function () {
    function Session(session, _a) {
        if (session === void 0) { session = {}; }
        var stickySession = _a.stickySession, sessionSampleRate = _a.sessionSampleRate, errorSampleRate = _a.errorSampleRate;
        var _b, _c, _d, _e;
        var now = new Date().getTime();
        this._id = session.id || uuid4();
        this._started = (_b = session.started) !== null && _b !== void 0 ? _b : now;
        this._lastActivity = (_c = session.lastActivity) !== null && _c !== void 0 ? _c : now;
        this._segmentId = (_d = session.segmentId) !== null && _d !== void 0 ? _d : 0;
        this._sampled =
            (_e = session.sampled) !== null && _e !== void 0 ? _e : (isSampled(sessionSampleRate)
                ? 'session'
                : isSampled(errorSampleRate)
                    ? 'error'
                    : false);
        this.options = {
            stickySession: stickySession,
        };
    }
    Object.defineProperty(Session.prototype, "id", {
        get: function () {
            return this._id;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Session.prototype, "started", {
        get: function () {
            return this._started;
        },
        set: function (newDate) {
            this._started = newDate;
            if (this.options.stickySession) {
                saveSession(this);
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Session.prototype, "lastActivity", {
        get: function () {
            return this._lastActivity;
        },
        set: function (newDate) {
            this._lastActivity = newDate;
            if (this.options.stickySession) {
                saveSession(this);
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Session.prototype, "segmentId", {
        get: function () {
            return this._segmentId;
        },
        set: function (id) {
            this._segmentId = id;
            if (this.options.stickySession) {
                saveSession(this);
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Session.prototype, "previousSessionId", {
        get: function () {
            return this._previousSessionId;
        },
        set: function (id) {
            this._previousSessionId = id;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Session.prototype, "sampled", {
        get: function () {
            return this._sampled;
        },
        set: function (_isSampled) {
            throw new Error('Unable to change sampled value');
        },
        enumerable: false,
        configurable: true
    });
    Session.prototype.toJSON = function () {
        return {
            id: this.id,
            started: this.started,
            lastActivity: this.lastActivity,
            segmentId: this._segmentId,
            sampled: this._sampled,
        };
    };
    return Session;
}());

/**
 * Create a new session, which in its current implementation is a Sentry event
 * that all replays will be saved to as attachments. Currently, we only expect
 * one of these Sentry events per "replay session".
 */
function createSession(_a) {
    var sessionSampleRate = _a.sessionSampleRate, errorSampleRate = _a.errorSampleRate, _b = _a.stickySession, stickySession = _b === void 0 ? false : _b;
    var session = new Session(undefined, {
        stickySession: stickySession,
        errorSampleRate: errorSampleRate,
        sessionSampleRate: sessionSampleRate,
    });
    logger.log("Creating new session: ".concat(session.id));
    if (stickySession) {
        saveSession(session);
    }
    return session;
}

/**
 * Fetches a session from storage
 */
function fetchSession(_a) {
    var sessionSampleRate = _a.sessionSampleRate, errorSampleRate = _a.errorSampleRate;
    var hasSessionStorage = 'sessionStorage' in window;
    if (!hasSessionStorage) {
        return null;
    }
    try {
        // This can throw if cookies are disabled
        var sessionStringFromStorage = window.sessionStorage.getItem(REPLAY_SESSION_KEY);
        if (!sessionStringFromStorage) {
            return null;
        }
        var sessionObj = JSON.parse(sessionStringFromStorage);
        return new Session(sessionObj, 
        // We are assuming that if there is a saved item, then the session is sticky,
        // however this could break down if we used a different storage mechanism (e.g. localstorage)
        { stickySession: true, sessionSampleRate: sessionSampleRate, errorSampleRate: errorSampleRate });
    }
    catch (_b) {
        return null;
    }
}

/**
 * Get or create a session
 */
function getSession(_a) {
    var expiry = _a.expiry, currentSession = _a.currentSession, stickySession = _a.stickySession, sessionSampleRate = _a.sessionSampleRate, errorSampleRate = _a.errorSampleRate;
    // If session exists and is passed, use it instead of always hitting session storage
    var session = currentSession ||
        (stickySession && fetchSession({ sessionSampleRate: sessionSampleRate, errorSampleRate: errorSampleRate }));
    if (session) {
        // If there is a session, check if it is valid (e.g. "last activity" time
        // should be within the "session idle time", and "session started" time is
        // within "max session time").
        var isExpired = isSessionExpired(session, expiry);
        if (!isExpired) {
            return { type: 'saved', session: session };
        }
        else {
            logger.log("Session has expired");
        }
        // Otherwise continue to create a new session
    }
    var newSession = createSession({
        stickySession: stickySession,
        sessionSampleRate: sessionSampleRate,
        errorSampleRate: errorSampleRate,
    });
    return { type: 'new', session: newSession };
}

/**
 * Wrapper for core SDK's `addBreadcrumb` only when used on `sentry.io`
 */
function addInternalBreadcrumb(arg) {
    if (!isInternal()) {
        return;
    }
    var category = arg.category, level = arg.level, message = arg.message, rest = __rest(arg, ["category", "level", "message"]);
    addBreadcrumb(__assign({ category: category || 'console', level: level || 'debug', message: "[debug]: ".concat(message) }, rest));
}

/**
 * Captures exceptions to Sentry only when it occurs on sentry.io
 */
function captureInternalException(err) {
    if (!isInternal()) {
        return;
    }
    captureException(err);
}

function createPayload(_a) {
    var events = _a.events, headers = _a.headers;
    var payloadWithSequence;
    // XXX: newline is needed to separate sequence id from events
    var replayHeaders = "".concat(JSON.stringify(headers), "\n");
    if (typeof events === 'string') {
        payloadWithSequence = "".concat(replayHeaders).concat(events);
    }
    else {
        var enc = new TextEncoder();
        // XXX: newline is needed to separate sequence id from events
        var sequence = enc.encode(replayHeaders);
        // Merge the two Uint8Arrays
        payloadWithSequence = new Uint8Array(sequence.length + events.length);
        payloadWithSequence.set(sequence);
        payloadWithSequence.set(events, sequence.length);
    }
    return payloadWithSequence;
}

var NAVIGATION_ENTRY_KEYS = [
    'name',
    'type',
    'startTime',
    'transferSize',
    'duration',
];
function isNavigationEntryEqual(a) {
    return function (b) {
        return NAVIGATION_ENTRY_KEYS.every(function (key) { return a[key] === b[key]; });
    };
}
/**
 * There are some difficulties diagnosing why there are duplicate navigation
 * entries. We've witnessed several intermittent results:
 * - duplicate entries have duration = 0
 * - duplicate entries are the same object reference
 * - none of the above
 *
 * Compare the values of several keys to determine if the entries are duplicates or not.
 */
function dedupePerformanceEntries(currentList, newList) {
    // Partition `currentList` into 3 different lists based on entryType
    var _a = currentList.reduce(function (acc, entry) {
        if (entry.entryType === 'navigation') {
            acc[0].push(entry);
        }
        else if (entry.entryType === 'largest-contentful-paint') {
            acc[1].push(entry);
        }
        else {
            acc[2].push(entry);
        }
        return acc;
    }, [[], [], []]), existingNavigationEntries = _a[0], existingLcpEntries = _a[1], existingEntries = _a[2];
    var newEntries = [];
    var newNavigationEntries = [];
    var newLcpEntry = existingLcpEntries.length
        ? existingLcpEntries[existingLcpEntries.length - 1] // Take the last element as list is sorted
        : undefined;
    newList.forEach(function (entry) {
        if (entry.entryType === 'largest-contentful-paint') {
            // We want the latest LCP event only
            if (!newLcpEntry || newLcpEntry.startTime < entry.startTime) {
                newLcpEntry = entry;
            }
            return;
        }
        if (entry.entryType === 'navigation') {
            var navigationEntry = entry;
            // Check if the navigation entry is contained in currentList or newList
            if (
            // Ignore any navigation entries with duration 0, as they are likely duplicates
            entry.duration > 0 &&
                // Ensure new entry does not already exist in existing entries
                !existingNavigationEntries.find(isNavigationEntryEqual(navigationEntry)) &&
                // Ensure new entry does not already exist in new list of navigation entries
                !newNavigationEntries.find(isNavigationEntryEqual(navigationEntry))) {
                newNavigationEntries.push(navigationEntry);
            }
            // Otherwise this navigation entry is considered a duplicate and is thrown away
            return;
        }
        newEntries.push(entry);
    });
    // Re-combine and sort by startTime
    return __spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray([], (newLcpEntry ? [newLcpEntry] : []), true), existingNavigationEntries, true), existingEntries, true), newEntries, true), newNavigationEntries, true).sort(function (a, b) { return a.startTime - b.startTime; });
}

var _a;
// Map entryType -> function to normalize data for event
var ENTRY_TYPES = (_a = {
        resource: createResourceEntry,
        paint: createPaintEntry,
        navigation: createNavigationEntry
    },
    _a['largest-contentful-paint'] = createLargestContentfulPaint,
    _a);
function createPerformanceEntries(entries) {
    return entries
        .map(createPerformanceEntry)
        .filter(Boolean);
}
function createPerformanceEntry(entry) {
    if (ENTRY_TYPES[entry.entryType] === undefined) {
        return null;
    }
    return ENTRY_TYPES[entry.entryType](entry);
}
function getAbsoluteTime(time) {
    // browserPerformanceTimeOrigin can be undefined if `performance` or
    // `performance.now` doesn't exist, but this is already checked by this integration
    return (((browserPerformanceTimeOrigin || window.performance.timeOrigin) + time) /
        1000);
}
function createPaintEntry(entry) {
    var duration = entry.duration, entryType = entry.entryType, name = entry.name, startTime = entry.startTime;
    var start = getAbsoluteTime(startTime);
    return {
        type: entryType,
        name: name,
        start: start,
        end: start + duration,
    };
}
function createNavigationEntry(entry) {
    // TODO: There looks to be some more interesting bits in here (domComplete, domContentLoaded)
    var entryType = entry.entryType, name = entry.name, duration = entry.duration, domComplete = entry.domComplete, startTime = entry.startTime, transferSize = entry.transferSize, type = entry.type;
    // Ignore entries with no duration, they do not seem to be useful and cause dupes
    if (duration === 0) {
        return null;
    }
    return {
        type: "".concat(entryType, ".").concat(type),
        start: getAbsoluteTime(startTime),
        end: getAbsoluteTime(domComplete),
        name: name,
        data: {
            size: transferSize,
            duration: duration,
        },
    };
}
function createResourceEntry(entry) {
    var entryType = entry.entryType, initiatorType = entry.initiatorType, name = entry.name, responseEnd = entry.responseEnd, startTime = entry.startTime, encodedBodySize = entry.encodedBodySize, transferSize = entry.transferSize;
    // Do not capture fetches to Sentry ingestion endpoint
    if (isIngestHost(name)) {
        return null;
    }
    // Core SDK handles these
    if (['fetch', 'xmlhttprequest'].includes(initiatorType)) {
        return null;
    }
    return {
        type: "".concat(entryType, ".").concat(initiatorType),
        start: getAbsoluteTime(startTime),
        end: getAbsoluteTime(responseEnd),
        name: name,
        data: {
            size: transferSize,
            encodedBodySize: encodedBodySize,
        },
    };
}
function createLargestContentfulPaint(entry) {
    var duration = entry.duration, entryType = entry.entryType, startTime = entry.startTime, size = entry.size;
    var start = getAbsoluteTime(startTime);
    return {
        type: entryType,
        name: entryType,
        start: start,
        end: start + duration,
        data: {
            duration: duration,
            size: size,
            // Not sure why this errors, Node should be correct (Argument of type 'Node' is not assignable to parameter of type 'INode')
            nodeId: record.mirror.getId(entry.element),
        },
    };
}
function createMemoryEntry(memoryEntry) {
    var jsHeapSizeLimit = memoryEntry.jsHeapSizeLimit, totalJSHeapSize = memoryEntry.totalJSHeapSize, usedJSHeapSize = memoryEntry.usedJSHeapSize;
    // we don't want to use `getAbsoluteTime` because it adds the event time to the
    // time origin, so we get the current timestamp instead
    var time = new Date().getTime() / 1000;
    return {
        type: 'memory',
        name: 'memory',
        start: time,
        end: time,
        data: {
            memory: {
                jsHeapSizeLimit: jsHeapSizeLimit,
                totalJSHeapSize: totalJSHeapSize,
                usedJSHeapSize: usedJSHeapSize,
            },
        },
    };
}

var workerString = `/*! pako 2.0.4 https://github.com/nodeca/pako @license (MIT AND Zlib) */
function t(t){let e=t.length;for(;--e>=0;)t[e]=0}const e=new Uint8Array([0,0,0,0,0,0,0,0,1,1,1,1,2,2,2,2,3,3,3,3,4,4,4,4,5,5,5,5,0]),a=new Uint8Array([0,0,0,0,1,1,2,2,3,3,4,4,5,5,6,6,7,7,8,8,9,9,10,10,11,11,12,12,13,13]),i=new Uint8Array([0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,2,3,7]),n=new Uint8Array([16,17,18,0,8,7,9,6,10,5,11,4,12,3,13,2,14,1,15]),s=new Array(576);t(s);const r=new Array(60);t(r);const o=new Array(512);t(o);const l=new Array(256);t(l);const h=new Array(29);t(h);const d=new Array(30);function _(t,e,a,i,n){this.static_tree=t,this.extra_bits=e,this.extra_base=a,this.elems=i,this.max_length=n,this.has_stree=t&&t.length}let f,c,u;function w(t,e){this.dyn_tree=t,this.max_code=0,this.stat_desc=e}t(d);const b=t=>t<256?o[t]:o[256+(t>>>7)],g=(t,e)=>{t.pending_buf[t.pending++]=255&e,t.pending_buf[t.pending++]=e>>>8&255},p=(t,e,a)=>{t.bi_valid>16-a?(t.bi_buf|=e<<t.bi_valid&65535,g(t,t.bi_buf),t.bi_buf=e>>16-t.bi_valid,t.bi_valid+=a-16):(t.bi_buf|=e<<t.bi_valid&65535,t.bi_valid+=a)},m=(t,e,a)=>{p(t,a[2*e],a[2*e+1])},k=(t,e)=>{let a=0;do{a|=1&t,t>>>=1,a<<=1}while(--e>0);return a>>>1},v=(t,e,a)=>{const i=new Array(16);let n,s,r=0;for(n=1;n<=15;n++)i[n]=r=r+a[n-1]<<1;for(s=0;s<=e;s++){let e=t[2*s+1];0!==e&&(t[2*s]=k(i[e]++,e))}},y=t=>{let e;for(e=0;e<286;e++)t.dyn_ltree[2*e]=0;for(e=0;e<30;e++)t.dyn_dtree[2*e]=0;for(e=0;e<19;e++)t.bl_tree[2*e]=0;t.dyn_ltree[512]=1,t.opt_len=t.static_len=0,t.last_lit=t.matches=0},x=t=>{t.bi_valid>8?g(t,t.bi_buf):t.bi_valid>0&&(t.pending_buf[t.pending++]=t.bi_buf),t.bi_buf=0,t.bi_valid=0},z=(t,e,a,i)=>{const n=2*e,s=2*a;return t[n]<t[s]||t[n]===t[s]&&i[e]<=i[a]},A=(t,e,a)=>{const i=t.heap[a];let n=a<<1;for(;n<=t.heap_len&&(n<t.heap_len&&z(e,t.heap[n+1],t.heap[n],t.depth)&&n++,!z(e,i,t.heap[n],t.depth));)t.heap[a]=t.heap[n],a=n,n<<=1;t.heap[a]=i},E=(t,i,n)=>{let s,r,o,_,f=0;if(0!==t.last_lit)do{s=t.pending_buf[t.d_buf+2*f]<<8|t.pending_buf[t.d_buf+2*f+1],r=t.pending_buf[t.l_buf+f],f++,0===s?m(t,r,i):(o=l[r],m(t,o+256+1,i),_=e[o],0!==_&&(r-=h[o],p(t,r,_)),s--,o=b(s),m(t,o,n),_=a[o],0!==_&&(s-=d[o],p(t,s,_)))}while(f<t.last_lit);m(t,256,i)},R=(t,e)=>{const a=e.dyn_tree,i=e.stat_desc.static_tree,n=e.stat_desc.has_stree,s=e.stat_desc.elems;let r,o,l,h=-1;for(t.heap_len=0,t.heap_max=573,r=0;r<s;r++)0!==a[2*r]?(t.heap[++t.heap_len]=h=r,t.depth[r]=0):a[2*r+1]=0;for(;t.heap_len<2;)l=t.heap[++t.heap_len]=h<2?++h:0,a[2*l]=1,t.depth[l]=0,t.opt_len--,n&&(t.static_len-=i[2*l+1]);for(e.max_code=h,r=t.heap_len>>1;r>=1;r--)A(t,a,r);l=s;do{r=t.heap[1],t.heap[1]=t.heap[t.heap_len--],A(t,a,1),o=t.heap[1],t.heap[--t.heap_max]=r,t.heap[--t.heap_max]=o,a[2*l]=a[2*r]+a[2*o],t.depth[l]=(t.depth[r]>=t.depth[o]?t.depth[r]:t.depth[o])+1,a[2*r+1]=a[2*o+1]=l,t.heap[1]=l++,A(t,a,1)}while(t.heap_len>=2);t.heap[--t.heap_max]=t.heap[1],((t,e)=>{const a=e.dyn_tree,i=e.max_code,n=e.stat_desc.static_tree,s=e.stat_desc.has_stree,r=e.stat_desc.extra_bits,o=e.stat_desc.extra_base,l=e.stat_desc.max_length;let h,d,_,f,c,u,w=0;for(f=0;f<=15;f++)t.bl_count[f]=0;for(a[2*t.heap[t.heap_max]+1]=0,h=t.heap_max+1;h<573;h++)d=t.heap[h],f=a[2*a[2*d+1]+1]+1,f>l&&(f=l,w++),a[2*d+1]=f,d>i||(t.bl_count[f]++,c=0,d>=o&&(c=r[d-o]),u=a[2*d],t.opt_len+=u*(f+c),s&&(t.static_len+=u*(n[2*d+1]+c)));if(0!==w){do{for(f=l-1;0===t.bl_count[f];)f--;t.bl_count[f]--,t.bl_count[f+1]+=2,t.bl_count[l]--,w-=2}while(w>0);for(f=l;0!==f;f--)for(d=t.bl_count[f];0!==d;)_=t.heap[--h],_>i||(a[2*_+1]!==f&&(t.opt_len+=(f-a[2*_+1])*a[2*_],a[2*_+1]=f),d--)}})(t,e),v(a,h,t.bl_count)},Z=(t,e,a)=>{let i,n,s=-1,r=e[1],o=0,l=7,h=4;for(0===r&&(l=138,h=3),e[2*(a+1)+1]=65535,i=0;i<=a;i++)n=r,r=e[2*(i+1)+1],++o<l&&n===r||(o<h?t.bl_tree[2*n]+=o:0!==n?(n!==s&&t.bl_tree[2*n]++,t.bl_tree[32]++):o<=10?t.bl_tree[34]++:t.bl_tree[36]++,o=0,s=n,0===r?(l=138,h=3):n===r?(l=6,h=3):(l=7,h=4))},S=(t,e,a)=>{let i,n,s=-1,r=e[1],o=0,l=7,h=4;for(0===r&&(l=138,h=3),i=0;i<=a;i++)if(n=r,r=e[2*(i+1)+1],!(++o<l&&n===r)){if(o<h)do{m(t,n,t.bl_tree)}while(0!=--o);else 0!==n?(n!==s&&(m(t,n,t.bl_tree),o--),m(t,16,t.bl_tree),p(t,o-3,2)):o<=10?(m(t,17,t.bl_tree),p(t,o-3,3)):(m(t,18,t.bl_tree),p(t,o-11,7));o=0,s=n,0===r?(l=138,h=3):n===r?(l=6,h=3):(l=7,h=4)}};let U=!1;const D=(t,e,a,i)=>{p(t,0+(i?1:0),3),((t,e,a,i)=>{x(t),i&&(g(t,a),g(t,~a)),t.pending_buf.set(t.window.subarray(e,e+a),t.pending),t.pending+=a})(t,e,a,!0)};var O=(t,e,a,i)=>{let o,l,h=0;t.level>0?(2===t.strm.data_type&&(t.strm.data_type=(t=>{let e,a=4093624447;for(e=0;e<=31;e++,a>>>=1)if(1&a&&0!==t.dyn_ltree[2*e])return 0;if(0!==t.dyn_ltree[18]||0!==t.dyn_ltree[20]||0!==t.dyn_ltree[26])return 1;for(e=32;e<256;e++)if(0!==t.dyn_ltree[2*e])return 1;return 0})(t)),R(t,t.l_desc),R(t,t.d_desc),h=(t=>{let e;for(Z(t,t.dyn_ltree,t.l_desc.max_code),Z(t,t.dyn_dtree,t.d_desc.max_code),R(t,t.bl_desc),e=18;e>=3&&0===t.bl_tree[2*n[e]+1];e--);return t.opt_len+=3*(e+1)+5+5+4,e})(t),o=t.opt_len+3+7>>>3,l=t.static_len+3+7>>>3,l<=o&&(o=l)):o=l=a+5,a+4<=o&&-1!==e?D(t,e,a,i):4===t.strategy||l===o?(p(t,2+(i?1:0),3),E(t,s,r)):(p(t,4+(i?1:0),3),((t,e,a,i)=>{let s;for(p(t,e-257,5),p(t,a-1,5),p(t,i-4,4),s=0;s<i;s++)p(t,t.bl_tree[2*n[s]+1],3);S(t,t.dyn_ltree,e-1),S(t,t.dyn_dtree,a-1)})(t,t.l_desc.max_code+1,t.d_desc.max_code+1,h+1),E(t,t.dyn_ltree,t.dyn_dtree)),y(t),i&&x(t)},T={_tr_init:t=>{U||((()=>{let t,n,w,b,g;const p=new Array(16);for(w=0,b=0;b<28;b++)for(h[b]=w,t=0;t<1<<e[b];t++)l[w++]=b;for(l[w-1]=b,g=0,b=0;b<16;b++)for(d[b]=g,t=0;t<1<<a[b];t++)o[g++]=b;for(g>>=7;b<30;b++)for(d[b]=g<<7,t=0;t<1<<a[b]-7;t++)o[256+g++]=b;for(n=0;n<=15;n++)p[n]=0;for(t=0;t<=143;)s[2*t+1]=8,t++,p[8]++;for(;t<=255;)s[2*t+1]=9,t++,p[9]++;for(;t<=279;)s[2*t+1]=7,t++,p[7]++;for(;t<=287;)s[2*t+1]=8,t++,p[8]++;for(v(s,287,p),t=0;t<30;t++)r[2*t+1]=5,r[2*t]=k(t,5);f=new _(s,e,257,286,15),c=new _(r,a,0,30,15),u=new _(new Array(0),i,0,19,7)})(),U=!0),t.l_desc=new w(t.dyn_ltree,f),t.d_desc=new w(t.dyn_dtree,c),t.bl_desc=new w(t.bl_tree,u),t.bi_buf=0,t.bi_valid=0,y(t)},_tr_stored_block:D,_tr_flush_block:O,_tr_tally:(t,e,a)=>(t.pending_buf[t.d_buf+2*t.last_lit]=e>>>8&255,t.pending_buf[t.d_buf+2*t.last_lit+1]=255&e,t.pending_buf[t.l_buf+t.last_lit]=255&a,t.last_lit++,0===e?t.dyn_ltree[2*a]++:(t.matches++,e--,t.dyn_ltree[2*(l[a]+256+1)]++,t.dyn_dtree[2*b(e)]++),t.last_lit===t.lit_bufsize-1),_tr_align:t=>{p(t,2,3),m(t,256,s),(t=>{16===t.bi_valid?(g(t,t.bi_buf),t.bi_buf=0,t.bi_valid=0):t.bi_valid>=8&&(t.pending_buf[t.pending++]=255&t.bi_buf,t.bi_buf>>=8,t.bi_valid-=8)})(t)}};var N=(t,e,a,i)=>{let n=65535&t|0,s=t>>>16&65535|0,r=0;for(;0!==a;){r=a>2e3?2e3:a,a-=r;do{n=n+e[i++]|0,s=s+n|0}while(--r);n%=65521,s%=65521}return n|s<<16|0};const F=new Uint32Array((()=>{let t,e=[];for(var a=0;a<256;a++){t=a;for(var i=0;i<8;i++)t=1&t?3988292384^t>>>1:t>>>1;e[a]=t}return e})());var L=(t,e,a,i)=>{const n=F,s=i+a;t^=-1;for(let a=i;a<s;a++)t=t>>>8^n[255&(t^e[a])];return-1^t},I={2:"need dictionary",1:"stream end",0:"","-1":"file error","-2":"stream error","-3":"data error","-4":"insufficient memory","-5":"buffer error","-6":"incompatible version"},B={Z_NO_FLUSH:0,Z_PARTIAL_FLUSH:1,Z_SYNC_FLUSH:2,Z_FULL_FLUSH:3,Z_FINISH:4,Z_BLOCK:5,Z_TREES:6,Z_OK:0,Z_STREAM_END:1,Z_NEED_DICT:2,Z_ERRNO:-1,Z_STREAM_ERROR:-2,Z_DATA_ERROR:-3,Z_MEM_ERROR:-4,Z_BUF_ERROR:-5,Z_NO_COMPRESSION:0,Z_BEST_SPEED:1,Z_BEST_COMPRESSION:9,Z_DEFAULT_COMPRESSION:-1,Z_FILTERED:1,Z_HUFFMAN_ONLY:2,Z_RLE:3,Z_FIXED:4,Z_DEFAULT_STRATEGY:0,Z_BINARY:0,Z_TEXT:1,Z_UNKNOWN:2,Z_DEFLATED:8};const{_tr_init:C,_tr_stored_block:H,_tr_flush_block:M,_tr_tally:j,_tr_align:K}=T,{Z_NO_FLUSH:P,Z_PARTIAL_FLUSH:Y,Z_FULL_FLUSH:G,Z_FINISH:X,Z_BLOCK:J,Z_OK:W,Z_STREAM_END:q,Z_STREAM_ERROR:Q,Z_DATA_ERROR:V,Z_BUF_ERROR:$,Z_DEFAULT_COMPRESSION:tt,Z_FILTERED:et,Z_HUFFMAN_ONLY:at,Z_RLE:it,Z_FIXED:nt,Z_DEFAULT_STRATEGY:st,Z_UNKNOWN:rt,Z_DEFLATED:ot}=B,lt=(t,e)=>(t.msg=I[e],e),ht=t=>(t<<1)-(t>4?9:0),dt=t=>{let e=t.length;for(;--e>=0;)t[e]=0};let _t=(t,e,a)=>(e<<t.hash_shift^a)&t.hash_mask;const ft=t=>{const e=t.state;let a=e.pending;a>t.avail_out&&(a=t.avail_out),0!==a&&(t.output.set(e.pending_buf.subarray(e.pending_out,e.pending_out+a),t.next_out),t.next_out+=a,e.pending_out+=a,t.total_out+=a,t.avail_out-=a,e.pending-=a,0===e.pending&&(e.pending_out=0))},ct=(t,e)=>{M(t,t.block_start>=0?t.block_start:-1,t.strstart-t.block_start,e),t.block_start=t.strstart,ft(t.strm)},ut=(t,e)=>{t.pending_buf[t.pending++]=e},wt=(t,e)=>{t.pending_buf[t.pending++]=e>>>8&255,t.pending_buf[t.pending++]=255&e},bt=(t,e,a,i)=>{let n=t.avail_in;return n>i&&(n=i),0===n?0:(t.avail_in-=n,e.set(t.input.subarray(t.next_in,t.next_in+n),a),1===t.state.wrap?t.adler=N(t.adler,e,n,a):2===t.state.wrap&&(t.adler=L(t.adler,e,n,a)),t.next_in+=n,t.total_in+=n,n)},gt=(t,e)=>{let a,i,n=t.max_chain_length,s=t.strstart,r=t.prev_length,o=t.nice_match;const l=t.strstart>t.w_size-262?t.strstart-(t.w_size-262):0,h=t.window,d=t.w_mask,_=t.prev,f=t.strstart+258;let c=h[s+r-1],u=h[s+r];t.prev_length>=t.good_match&&(n>>=2),o>t.lookahead&&(o=t.lookahead);do{if(a=e,h[a+r]===u&&h[a+r-1]===c&&h[a]===h[s]&&h[++a]===h[s+1]){s+=2,a++;do{}while(h[++s]===h[++a]&&h[++s]===h[++a]&&h[++s]===h[++a]&&h[++s]===h[++a]&&h[++s]===h[++a]&&h[++s]===h[++a]&&h[++s]===h[++a]&&h[++s]===h[++a]&&s<f);if(i=258-(f-s),s=f-258,i>r){if(t.match_start=e,r=i,i>=o)break;c=h[s+r-1],u=h[s+r]}}}while((e=_[e&d])>l&&0!=--n);return r<=t.lookahead?r:t.lookahead},pt=t=>{const e=t.w_size;let a,i,n,s,r;do{if(s=t.window_size-t.lookahead-t.strstart,t.strstart>=e+(e-262)){t.window.set(t.window.subarray(e,e+e),0),t.match_start-=e,t.strstart-=e,t.block_start-=e,i=t.hash_size,a=i;do{n=t.head[--a],t.head[a]=n>=e?n-e:0}while(--i);i=e,a=i;do{n=t.prev[--a],t.prev[a]=n>=e?n-e:0}while(--i);s+=e}if(0===t.strm.avail_in)break;if(i=bt(t.strm,t.window,t.strstart+t.lookahead,s),t.lookahead+=i,t.lookahead+t.insert>=3)for(r=t.strstart-t.insert,t.ins_h=t.window[r],t.ins_h=_t(t,t.ins_h,t.window[r+1]);t.insert&&(t.ins_h=_t(t,t.ins_h,t.window[r+3-1]),t.prev[r&t.w_mask]=t.head[t.ins_h],t.head[t.ins_h]=r,r++,t.insert--,!(t.lookahead+t.insert<3)););}while(t.lookahead<262&&0!==t.strm.avail_in)},mt=(t,e)=>{let a,i;for(;;){if(t.lookahead<262){if(pt(t),t.lookahead<262&&e===P)return 1;if(0===t.lookahead)break}if(a=0,t.lookahead>=3&&(t.ins_h=_t(t,t.ins_h,t.window[t.strstart+3-1]),a=t.prev[t.strstart&t.w_mask]=t.head[t.ins_h],t.head[t.ins_h]=t.strstart),0!==a&&t.strstart-a<=t.w_size-262&&(t.match_length=gt(t,a)),t.match_length>=3)if(i=j(t,t.strstart-t.match_start,t.match_length-3),t.lookahead-=t.match_length,t.match_length<=t.max_lazy_match&&t.lookahead>=3){t.match_length--;do{t.strstart++,t.ins_h=_t(t,t.ins_h,t.window[t.strstart+3-1]),a=t.prev[t.strstart&t.w_mask]=t.head[t.ins_h],t.head[t.ins_h]=t.strstart}while(0!=--t.match_length);t.strstart++}else t.strstart+=t.match_length,t.match_length=0,t.ins_h=t.window[t.strstart],t.ins_h=_t(t,t.ins_h,t.window[t.strstart+1]);else i=j(t,0,t.window[t.strstart]),t.lookahead--,t.strstart++;if(i&&(ct(t,!1),0===t.strm.avail_out))return 1}return t.insert=t.strstart<2?t.strstart:2,e===X?(ct(t,!0),0===t.strm.avail_out?3:4):t.last_lit&&(ct(t,!1),0===t.strm.avail_out)?1:2},kt=(t,e)=>{let a,i,n;for(;;){if(t.lookahead<262){if(pt(t),t.lookahead<262&&e===P)return 1;if(0===t.lookahead)break}if(a=0,t.lookahead>=3&&(t.ins_h=_t(t,t.ins_h,t.window[t.strstart+3-1]),a=t.prev[t.strstart&t.w_mask]=t.head[t.ins_h],t.head[t.ins_h]=t.strstart),t.prev_length=t.match_length,t.prev_match=t.match_start,t.match_length=2,0!==a&&t.prev_length<t.max_lazy_match&&t.strstart-a<=t.w_size-262&&(t.match_length=gt(t,a),t.match_length<=5&&(t.strategy===et||3===t.match_length&&t.strstart-t.match_start>4096)&&(t.match_length=2)),t.prev_length>=3&&t.match_length<=t.prev_length){n=t.strstart+t.lookahead-3,i=j(t,t.strstart-1-t.prev_match,t.prev_length-3),t.lookahead-=t.prev_length-1,t.prev_length-=2;do{++t.strstart<=n&&(t.ins_h=_t(t,t.ins_h,t.window[t.strstart+3-1]),a=t.prev[t.strstart&t.w_mask]=t.head[t.ins_h],t.head[t.ins_h]=t.strstart)}while(0!=--t.prev_length);if(t.match_available=0,t.match_length=2,t.strstart++,i&&(ct(t,!1),0===t.strm.avail_out))return 1}else if(t.match_available){if(i=j(t,0,t.window[t.strstart-1]),i&&ct(t,!1),t.strstart++,t.lookahead--,0===t.strm.avail_out)return 1}else t.match_available=1,t.strstart++,t.lookahead--}return t.match_available&&(i=j(t,0,t.window[t.strstart-1]),t.match_available=0),t.insert=t.strstart<2?t.strstart:2,e===X?(ct(t,!0),0===t.strm.avail_out?3:4):t.last_lit&&(ct(t,!1),0===t.strm.avail_out)?1:2};function vt(t,e,a,i,n){this.good_length=t,this.max_lazy=e,this.nice_length=a,this.max_chain=i,this.func=n}const yt=[new vt(0,0,0,0,((t,e)=>{let a=65535;for(a>t.pending_buf_size-5&&(a=t.pending_buf_size-5);;){if(t.lookahead<=1){if(pt(t),0===t.lookahead&&e===P)return 1;if(0===t.lookahead)break}t.strstart+=t.lookahead,t.lookahead=0;const i=t.block_start+a;if((0===t.strstart||t.strstart>=i)&&(t.lookahead=t.strstart-i,t.strstart=i,ct(t,!1),0===t.strm.avail_out))return 1;if(t.strstart-t.block_start>=t.w_size-262&&(ct(t,!1),0===t.strm.avail_out))return 1}return t.insert=0,e===X?(ct(t,!0),0===t.strm.avail_out?3:4):(t.strstart>t.block_start&&(ct(t,!1),t.strm.avail_out),1)})),new vt(4,4,8,4,mt),new vt(4,5,16,8,mt),new vt(4,6,32,32,mt),new vt(4,4,16,16,kt),new vt(8,16,32,32,kt),new vt(8,16,128,128,kt),new vt(8,32,128,256,kt),new vt(32,128,258,1024,kt),new vt(32,258,258,4096,kt)];function xt(){this.strm=null,this.status=0,this.pending_buf=null,this.pending_buf_size=0,this.pending_out=0,this.pending=0,this.wrap=0,this.gzhead=null,this.gzindex=0,this.method=ot,this.last_flush=-1,this.w_size=0,this.w_bits=0,this.w_mask=0,this.window=null,this.window_size=0,this.prev=null,this.head=null,this.ins_h=0,this.hash_size=0,this.hash_bits=0,this.hash_mask=0,this.hash_shift=0,this.block_start=0,this.match_length=0,this.prev_match=0,this.match_available=0,this.strstart=0,this.match_start=0,this.lookahead=0,this.prev_length=0,this.max_chain_length=0,this.max_lazy_match=0,this.level=0,this.strategy=0,this.good_match=0,this.nice_match=0,this.dyn_ltree=new Uint16Array(1146),this.dyn_dtree=new Uint16Array(122),this.bl_tree=new Uint16Array(78),dt(this.dyn_ltree),dt(this.dyn_dtree),dt(this.bl_tree),this.l_desc=null,this.d_desc=null,this.bl_desc=null,this.bl_count=new Uint16Array(16),this.heap=new Uint16Array(573),dt(this.heap),this.heap_len=0,this.heap_max=0,this.depth=new Uint16Array(573),dt(this.depth),this.l_buf=0,this.lit_bufsize=0,this.last_lit=0,this.d_buf=0,this.opt_len=0,this.static_len=0,this.matches=0,this.insert=0,this.bi_buf=0,this.bi_valid=0}const zt=t=>{if(!t||!t.state)return lt(t,Q);t.total_in=t.total_out=0,t.data_type=rt;const e=t.state;return e.pending=0,e.pending_out=0,e.wrap<0&&(e.wrap=-e.wrap),e.status=e.wrap?42:113,t.adler=2===e.wrap?0:1,e.last_flush=P,C(e),W},At=t=>{const e=zt(t);var a;return e===W&&((a=t.state).window_size=2*a.w_size,dt(a.head),a.max_lazy_match=yt[a.level].max_lazy,a.good_match=yt[a.level].good_length,a.nice_match=yt[a.level].nice_length,a.max_chain_length=yt[a.level].max_chain,a.strstart=0,a.block_start=0,a.lookahead=0,a.insert=0,a.match_length=a.prev_length=2,a.match_available=0,a.ins_h=0),e},Et=(t,e,a,i,n,s)=>{if(!t)return Q;let r=1;if(e===tt&&(e=6),i<0?(r=0,i=-i):i>15&&(r=2,i-=16),n<1||n>9||a!==ot||i<8||i>15||e<0||e>9||s<0||s>nt)return lt(t,Q);8===i&&(i=9);const o=new xt;return t.state=o,o.strm=t,o.wrap=r,o.gzhead=null,o.w_bits=i,o.w_size=1<<o.w_bits,o.w_mask=o.w_size-1,o.hash_bits=n+7,o.hash_size=1<<o.hash_bits,o.hash_mask=o.hash_size-1,o.hash_shift=~~((o.hash_bits+3-1)/3),o.window=new Uint8Array(2*o.w_size),o.head=new Uint16Array(o.hash_size),o.prev=new Uint16Array(o.w_size),o.lit_bufsize=1<<n+6,o.pending_buf_size=4*o.lit_bufsize,o.pending_buf=new Uint8Array(o.pending_buf_size),o.d_buf=1*o.lit_bufsize,o.l_buf=3*o.lit_bufsize,o.level=e,o.strategy=s,o.method=a,At(t)};var Rt={deflateInit:(t,e)=>Et(t,e,ot,15,8,st),deflateInit2:Et,deflateReset:At,deflateResetKeep:zt,deflateSetHeader:(t,e)=>t&&t.state?2!==t.state.wrap?Q:(t.state.gzhead=e,W):Q,deflate:(t,e)=>{let a,i;if(!t||!t.state||e>J||e<0)return t?lt(t,Q):Q;const n=t.state;if(!t.output||!t.input&&0!==t.avail_in||666===n.status&&e!==X)return lt(t,0===t.avail_out?$:Q);n.strm=t;const s=n.last_flush;if(n.last_flush=e,42===n.status)if(2===n.wrap)t.adler=0,ut(n,31),ut(n,139),ut(n,8),n.gzhead?(ut(n,(n.gzhead.text?1:0)+(n.gzhead.hcrc?2:0)+(n.gzhead.extra?4:0)+(n.gzhead.name?8:0)+(n.gzhead.comment?16:0)),ut(n,255&n.gzhead.time),ut(n,n.gzhead.time>>8&255),ut(n,n.gzhead.time>>16&255),ut(n,n.gzhead.time>>24&255),ut(n,9===n.level?2:n.strategy>=at||n.level<2?4:0),ut(n,255&n.gzhead.os),n.gzhead.extra&&n.gzhead.extra.length&&(ut(n,255&n.gzhead.extra.length),ut(n,n.gzhead.extra.length>>8&255)),n.gzhead.hcrc&&(t.adler=L(t.adler,n.pending_buf,n.pending,0)),n.gzindex=0,n.status=69):(ut(n,0),ut(n,0),ut(n,0),ut(n,0),ut(n,0),ut(n,9===n.level?2:n.strategy>=at||n.level<2?4:0),ut(n,3),n.status=113);else{let e=ot+(n.w_bits-8<<4)<<8,a=-1;a=n.strategy>=at||n.level<2?0:n.level<6?1:6===n.level?2:3,e|=a<<6,0!==n.strstart&&(e|=32),e+=31-e%31,n.status=113,wt(n,e),0!==n.strstart&&(wt(n,t.adler>>>16),wt(n,65535&t.adler)),t.adler=1}if(69===n.status)if(n.gzhead.extra){for(a=n.pending;n.gzindex<(65535&n.gzhead.extra.length)&&(n.pending!==n.pending_buf_size||(n.gzhead.hcrc&&n.pending>a&&(t.adler=L(t.adler,n.pending_buf,n.pending-a,a)),ft(t),a=n.pending,n.pending!==n.pending_buf_size));)ut(n,255&n.gzhead.extra[n.gzindex]),n.gzindex++;n.gzhead.hcrc&&n.pending>a&&(t.adler=L(t.adler,n.pending_buf,n.pending-a,a)),n.gzindex===n.gzhead.extra.length&&(n.gzindex=0,n.status=73)}else n.status=73;if(73===n.status)if(n.gzhead.name){a=n.pending;do{if(n.pending===n.pending_buf_size&&(n.gzhead.hcrc&&n.pending>a&&(t.adler=L(t.adler,n.pending_buf,n.pending-a,a)),ft(t),a=n.pending,n.pending===n.pending_buf_size)){i=1;break}i=n.gzindex<n.gzhead.name.length?255&n.gzhead.name.charCodeAt(n.gzindex++):0,ut(n,i)}while(0!==i);n.gzhead.hcrc&&n.pending>a&&(t.adler=L(t.adler,n.pending_buf,n.pending-a,a)),0===i&&(n.gzindex=0,n.status=91)}else n.status=91;if(91===n.status)if(n.gzhead.comment){a=n.pending;do{if(n.pending===n.pending_buf_size&&(n.gzhead.hcrc&&n.pending>a&&(t.adler=L(t.adler,n.pending_buf,n.pending-a,a)),ft(t),a=n.pending,n.pending===n.pending_buf_size)){i=1;break}i=n.gzindex<n.gzhead.comment.length?255&n.gzhead.comment.charCodeAt(n.gzindex++):0,ut(n,i)}while(0!==i);n.gzhead.hcrc&&n.pending>a&&(t.adler=L(t.adler,n.pending_buf,n.pending-a,a)),0===i&&(n.status=103)}else n.status=103;if(103===n.status&&(n.gzhead.hcrc?(n.pending+2>n.pending_buf_size&&ft(t),n.pending+2<=n.pending_buf_size&&(ut(n,255&t.adler),ut(n,t.adler>>8&255),t.adler=0,n.status=113)):n.status=113),0!==n.pending){if(ft(t),0===t.avail_out)return n.last_flush=-1,W}else if(0===t.avail_in&&ht(e)<=ht(s)&&e!==X)return lt(t,$);if(666===n.status&&0!==t.avail_in)return lt(t,$);if(0!==t.avail_in||0!==n.lookahead||e!==P&&666!==n.status){let a=n.strategy===at?((t,e)=>{let a;for(;;){if(0===t.lookahead&&(pt(t),0===t.lookahead)){if(e===P)return 1;break}if(t.match_length=0,a=j(t,0,t.window[t.strstart]),t.lookahead--,t.strstart++,a&&(ct(t,!1),0===t.strm.avail_out))return 1}return t.insert=0,e===X?(ct(t,!0),0===t.strm.avail_out?3:4):t.last_lit&&(ct(t,!1),0===t.strm.avail_out)?1:2})(n,e):n.strategy===it?((t,e)=>{let a,i,n,s;const r=t.window;for(;;){if(t.lookahead<=258){if(pt(t),t.lookahead<=258&&e===P)return 1;if(0===t.lookahead)break}if(t.match_length=0,t.lookahead>=3&&t.strstart>0&&(n=t.strstart-1,i=r[n],i===r[++n]&&i===r[++n]&&i===r[++n])){s=t.strstart+258;do{}while(i===r[++n]&&i===r[++n]&&i===r[++n]&&i===r[++n]&&i===r[++n]&&i===r[++n]&&i===r[++n]&&i===r[++n]&&n<s);t.match_length=258-(s-n),t.match_length>t.lookahead&&(t.match_length=t.lookahead)}if(t.match_length>=3?(a=j(t,1,t.match_length-3),t.lookahead-=t.match_length,t.strstart+=t.match_length,t.match_length=0):(a=j(t,0,t.window[t.strstart]),t.lookahead--,t.strstart++),a&&(ct(t,!1),0===t.strm.avail_out))return 1}return t.insert=0,e===X?(ct(t,!0),0===t.strm.avail_out?3:4):t.last_lit&&(ct(t,!1),0===t.strm.avail_out)?1:2})(n,e):yt[n.level].func(n,e);if(3!==a&&4!==a||(n.status=666),1===a||3===a)return 0===t.avail_out&&(n.last_flush=-1),W;if(2===a&&(e===Y?K(n):e!==J&&(H(n,0,0,!1),e===G&&(dt(n.head),0===n.lookahead&&(n.strstart=0,n.block_start=0,n.insert=0))),ft(t),0===t.avail_out))return n.last_flush=-1,W}return e!==X?W:n.wrap<=0?q:(2===n.wrap?(ut(n,255&t.adler),ut(n,t.adler>>8&255),ut(n,t.adler>>16&255),ut(n,t.adler>>24&255),ut(n,255&t.total_in),ut(n,t.total_in>>8&255),ut(n,t.total_in>>16&255),ut(n,t.total_in>>24&255)):(wt(n,t.adler>>>16),wt(n,65535&t.adler)),ft(t),n.wrap>0&&(n.wrap=-n.wrap),0!==n.pending?W:q)},deflateEnd:t=>{if(!t||!t.state)return Q;const e=t.state.status;return 42!==e&&69!==e&&73!==e&&91!==e&&103!==e&&113!==e&&666!==e?lt(t,Q):(t.state=null,113===e?lt(t,V):W)},deflateSetDictionary:(t,e)=>{let a=e.length;if(!t||!t.state)return Q;const i=t.state,n=i.wrap;if(2===n||1===n&&42!==i.status||i.lookahead)return Q;if(1===n&&(t.adler=N(t.adler,e,a,0)),i.wrap=0,a>=i.w_size){0===n&&(dt(i.head),i.strstart=0,i.block_start=0,i.insert=0);let t=new Uint8Array(i.w_size);t.set(e.subarray(a-i.w_size,a),0),e=t,a=i.w_size}const s=t.avail_in,r=t.next_in,o=t.input;for(t.avail_in=a,t.next_in=0,t.input=e,pt(i);i.lookahead>=3;){let t=i.strstart,e=i.lookahead-2;do{i.ins_h=_t(i,i.ins_h,i.window[t+3-1]),i.prev[t&i.w_mask]=i.head[i.ins_h],i.head[i.ins_h]=t,t++}while(--e);i.strstart=t,i.lookahead=2,pt(i)}return i.strstart+=i.lookahead,i.block_start=i.strstart,i.insert=i.lookahead,i.lookahead=0,i.match_length=i.prev_length=2,i.match_available=0,t.next_in=r,t.input=o,t.avail_in=s,i.wrap=n,W},deflateInfo:"pako deflate (from Nodeca project)"};const Zt=(t,e)=>Object.prototype.hasOwnProperty.call(t,e);var St=function(t){const e=Array.prototype.slice.call(arguments,1);for(;e.length;){const a=e.shift();if(a){if("object"!=typeof a)throw new TypeError(a+"must be non-object");for(const e in a)Zt(a,e)&&(t[e]=a[e])}}return t},Ut=t=>{let e=0;for(let a=0,i=t.length;a<i;a++)e+=t[a].length;const a=new Uint8Array(e);for(let e=0,i=0,n=t.length;e<n;e++){let n=t[e];a.set(n,i),i+=n.length}return a};let Dt=!0;try{String.fromCharCode.apply(null,new Uint8Array(1))}catch(t){Dt=!1}const Ot=new Uint8Array(256);for(let t=0;t<256;t++)Ot[t]=t>=252?6:t>=248?5:t>=240?4:t>=224?3:t>=192?2:1;Ot[254]=Ot[254]=1;var Tt=t=>{if("function"==typeof TextEncoder&&TextEncoder.prototype.encode)return(new TextEncoder).encode(t);let e,a,i,n,s,r=t.length,o=0;for(n=0;n<r;n++)a=t.charCodeAt(n),55296==(64512&a)&&n+1<r&&(i=t.charCodeAt(n+1),56320==(64512&i)&&(a=65536+(a-55296<<10)+(i-56320),n++)),o+=a<128?1:a<2048?2:a<65536?3:4;for(e=new Uint8Array(o),s=0,n=0;s<o;n++)a=t.charCodeAt(n),55296==(64512&a)&&n+1<r&&(i=t.charCodeAt(n+1),56320==(64512&i)&&(a=65536+(a-55296<<10)+(i-56320),n++)),a<128?e[s++]=a:a<2048?(e[s++]=192|a>>>6,e[s++]=128|63&a):a<65536?(e[s++]=224|a>>>12,e[s++]=128|a>>>6&63,e[s++]=128|63&a):(e[s++]=240|a>>>18,e[s++]=128|a>>>12&63,e[s++]=128|a>>>6&63,e[s++]=128|63&a);return e},Nt=(t,e)=>{const a=e||t.length;if("function"==typeof TextDecoder&&TextDecoder.prototype.decode)return(new TextDecoder).decode(t.subarray(0,e));let i,n;const s=new Array(2*a);for(n=0,i=0;i<a;){let e=t[i++];if(e<128){s[n++]=e;continue}let r=Ot[e];if(r>4)s[n++]=65533,i+=r-1;else{for(e&=2===r?31:3===r?15:7;r>1&&i<a;)e=e<<6|63&t[i++],r--;r>1?s[n++]=65533:e<65536?s[n++]=e:(e-=65536,s[n++]=55296|e>>10&1023,s[n++]=56320|1023&e)}}return((t,e)=>{if(e<65534&&t.subarray&&Dt)return String.fromCharCode.apply(null,t.length===e?t:t.subarray(0,e));let a="";for(let i=0;i<e;i++)a+=String.fromCharCode(t[i]);return a})(s,n)},Ft=(t,e)=>{(e=e||t.length)>t.length&&(e=t.length);let a=e-1;for(;a>=0&&128==(192&t[a]);)a--;return a<0||0===a?e:a+Ot[t[a]]>e?a:e};var Lt=function(){this.input=null,this.next_in=0,this.avail_in=0,this.total_in=0,this.output=null,this.next_out=0,this.avail_out=0,this.total_out=0,this.msg="",this.state=null,this.data_type=2,this.adler=0};const It=Object.prototype.toString,{Z_NO_FLUSH:Bt,Z_SYNC_FLUSH:Ct,Z_FULL_FLUSH:Ht,Z_FINISH:Mt,Z_OK:jt,Z_STREAM_END:Kt,Z_DEFAULT_COMPRESSION:Pt,Z_DEFAULT_STRATEGY:Yt,Z_DEFLATED:Gt}=B;function Xt(t){this.options=St({level:Pt,method:Gt,chunkSize:16384,windowBits:15,memLevel:8,strategy:Yt},t||{});let e=this.options;e.raw&&e.windowBits>0?e.windowBits=-e.windowBits:e.gzip&&e.windowBits>0&&e.windowBits<16&&(e.windowBits+=16),this.err=0,this.msg="",this.ended=!1,this.chunks=[],this.strm=new Lt,this.strm.avail_out=0;let a=Rt.deflateInit2(this.strm,e.level,e.method,e.windowBits,e.memLevel,e.strategy);if(a!==jt)throw new Error(I[a]);if(e.header&&Rt.deflateSetHeader(this.strm,e.header),e.dictionary){let t;if(t="string"==typeof e.dictionary?Tt(e.dictionary):"[object ArrayBuffer]"===It.call(e.dictionary)?new Uint8Array(e.dictionary):e.dictionary,a=Rt.deflateSetDictionary(this.strm,t),a!==jt)throw new Error(I[a]);this._dict_set=!0}}function Jt(t,e){const a=new Xt(e);if(a.push(t,!0),a.err)throw a.msg||I[a.err];return a.result}Xt.prototype.push=function(t,e){const a=this.strm,i=this.options.chunkSize;let n,s;if(this.ended)return!1;for(s=e===~~e?e:!0===e?Mt:Bt,"string"==typeof t?a.input=Tt(t):"[object ArrayBuffer]"===It.call(t)?a.input=new Uint8Array(t):a.input=t,a.next_in=0,a.avail_in=a.input.length;;)if(0===a.avail_out&&(a.output=new Uint8Array(i),a.next_out=0,a.avail_out=i),(s===Ct||s===Ht)&&a.avail_out<=6)this.onData(a.output.subarray(0,a.next_out)),a.avail_out=0;else{if(n=Rt.deflate(a,s),n===Kt)return a.next_out>0&&this.onData(a.output.subarray(0,a.next_out)),n=Rt.deflateEnd(this.strm),this.onEnd(n),this.ended=!0,n===jt;if(0!==a.avail_out){if(s>0&&a.next_out>0)this.onData(a.output.subarray(0,a.next_out)),a.avail_out=0;else if(0===a.avail_in)break}else this.onData(a.output)}return!0},Xt.prototype.onData=function(t){this.chunks.push(t)},Xt.prototype.onEnd=function(t){t===jt&&(this.result=Ut(this.chunks)),this.chunks=[],this.err=t,this.msg=this.strm.msg};var Wt={Deflate:Xt,deflate:Jt,deflateRaw:function(t,e){return(e=e||{}).raw=!0,Jt(t,e)},gzip:function(t,e){return(e=e||{}).gzip=!0,Jt(t,e)},constants:B};var qt=function(t,e){let a,i,n,s,r,o,l,h,d,_,f,c,u,w,b,g,p,m,k,v,y,x,z,A;const E=t.state;a=t.next_in,z=t.input,i=a+(t.avail_in-5),n=t.next_out,A=t.output,s=n-(e-t.avail_out),r=n+(t.avail_out-257),o=E.dmax,l=E.wsize,h=E.whave,d=E.wnext,_=E.window,f=E.hold,c=E.bits,u=E.lencode,w=E.distcode,b=(1<<E.lenbits)-1,g=(1<<E.distbits)-1;t:do{c<15&&(f+=z[a++]<<c,c+=8,f+=z[a++]<<c,c+=8),p=u[f&b];e:for(;;){if(m=p>>>24,f>>>=m,c-=m,m=p>>>16&255,0===m)A[n++]=65535&p;else{if(!(16&m)){if(0==(64&m)){p=u[(65535&p)+(f&(1<<m)-1)];continue e}if(32&m){E.mode=12;break t}t.msg="invalid literal/length code",E.mode=30;break t}k=65535&p,m&=15,m&&(c<m&&(f+=z[a++]<<c,c+=8),k+=f&(1<<m)-1,f>>>=m,c-=m),c<15&&(f+=z[a++]<<c,c+=8,f+=z[a++]<<c,c+=8),p=w[f&g];a:for(;;){if(m=p>>>24,f>>>=m,c-=m,m=p>>>16&255,!(16&m)){if(0==(64&m)){p=w[(65535&p)+(f&(1<<m)-1)];continue a}t.msg="invalid distance code",E.mode=30;break t}if(v=65535&p,m&=15,c<m&&(f+=z[a++]<<c,c+=8,c<m&&(f+=z[a++]<<c,c+=8)),v+=f&(1<<m)-1,v>o){t.msg="invalid distance too far back",E.mode=30;break t}if(f>>>=m,c-=m,m=n-s,v>m){if(m=v-m,m>h&&E.sane){t.msg="invalid distance too far back",E.mode=30;break t}if(y=0,x=_,0===d){if(y+=l-m,m<k){k-=m;do{A[n++]=_[y++]}while(--m);y=n-v,x=A}}else if(d<m){if(y+=l+d-m,m-=d,m<k){k-=m;do{A[n++]=_[y++]}while(--m);if(y=0,d<k){m=d,k-=m;do{A[n++]=_[y++]}while(--m);y=n-v,x=A}}}else if(y+=d-m,m<k){k-=m;do{A[n++]=_[y++]}while(--m);y=n-v,x=A}for(;k>2;)A[n++]=x[y++],A[n++]=x[y++],A[n++]=x[y++],k-=3;k&&(A[n++]=x[y++],k>1&&(A[n++]=x[y++]))}else{y=n-v;do{A[n++]=A[y++],A[n++]=A[y++],A[n++]=A[y++],k-=3}while(k>2);k&&(A[n++]=A[y++],k>1&&(A[n++]=A[y++]))}break}}break}}while(a<i&&n<r);k=c>>3,a-=k,c-=k<<3,f&=(1<<c)-1,t.next_in=a,t.next_out=n,t.avail_in=a<i?i-a+5:5-(a-i),t.avail_out=n<r?r-n+257:257-(n-r),E.hold=f,E.bits=c};const Qt=new Uint16Array([3,4,5,6,7,8,9,10,11,13,15,17,19,23,27,31,35,43,51,59,67,83,99,115,131,163,195,227,258,0,0]),Vt=new Uint8Array([16,16,16,16,16,16,16,16,17,17,17,17,18,18,18,18,19,19,19,19,20,20,20,20,21,21,21,21,16,72,78]),$t=new Uint16Array([1,2,3,4,5,7,9,13,17,25,33,49,65,97,129,193,257,385,513,769,1025,1537,2049,3073,4097,6145,8193,12289,16385,24577,0,0]),te=new Uint8Array([16,16,16,16,17,17,18,18,19,19,20,20,21,21,22,22,23,23,24,24,25,25,26,26,27,27,28,28,29,29,64,64]);var ee=(t,e,a,i,n,s,r,o)=>{const l=o.bits;let h,d,_,f,c,u,w=0,b=0,g=0,p=0,m=0,k=0,v=0,y=0,x=0,z=0,A=null,E=0;const R=new Uint16Array(16),Z=new Uint16Array(16);let S,U,D,O=null,T=0;for(w=0;w<=15;w++)R[w]=0;for(b=0;b<i;b++)R[e[a+b]]++;for(m=l,p=15;p>=1&&0===R[p];p--);if(m>p&&(m=p),0===p)return n[s++]=20971520,n[s++]=20971520,o.bits=1,0;for(g=1;g<p&&0===R[g];g++);for(m<g&&(m=g),y=1,w=1;w<=15;w++)if(y<<=1,y-=R[w],y<0)return-1;if(y>0&&(0===t||1!==p))return-1;for(Z[1]=0,w=1;w<15;w++)Z[w+1]=Z[w]+R[w];for(b=0;b<i;b++)0!==e[a+b]&&(r[Z[e[a+b]]++]=b);if(0===t?(A=O=r,u=19):1===t?(A=Qt,E-=257,O=Vt,T-=257,u=256):(A=$t,O=te,u=-1),z=0,b=0,w=g,c=s,k=m,v=0,_=-1,x=1<<m,f=x-1,1===t&&x>852||2===t&&x>592)return 1;for(;;){S=w-v,r[b]<u?(U=0,D=r[b]):r[b]>u?(U=O[T+r[b]],D=A[E+r[b]]):(U=96,D=0),h=1<<w-v,d=1<<k,g=d;do{d-=h,n[c+(z>>v)+d]=S<<24|U<<16|D|0}while(0!==d);for(h=1<<w-1;z&h;)h>>=1;if(0!==h?(z&=h-1,z+=h):z=0,b++,0==--R[w]){if(w===p)break;w=e[a+r[b]]}if(w>m&&(z&f)!==_){for(0===v&&(v=m),c+=g,k=w-v,y=1<<k;k+v<p&&(y-=R[k+v],!(y<=0));)k++,y<<=1;if(x+=1<<k,1===t&&x>852||2===t&&x>592)return 1;_=z&f,n[_]=m<<24|k<<16|c-s|0}}return 0!==z&&(n[c+z]=w-v<<24|64<<16|0),o.bits=m,0};const{Z_FINISH:ae,Z_BLOCK:ie,Z_TREES:ne,Z_OK:se,Z_STREAM_END:re,Z_NEED_DICT:oe,Z_STREAM_ERROR:le,Z_DATA_ERROR:he,Z_MEM_ERROR:de,Z_BUF_ERROR:_e,Z_DEFLATED:fe}=B,ce=t=>(t>>>24&255)+(t>>>8&65280)+((65280&t)<<8)+((255&t)<<24);function ue(){this.mode=0,this.last=!1,this.wrap=0,this.havedict=!1,this.flags=0,this.dmax=0,this.check=0,this.total=0,this.head=null,this.wbits=0,this.wsize=0,this.whave=0,this.wnext=0,this.window=null,this.hold=0,this.bits=0,this.length=0,this.offset=0,this.extra=0,this.lencode=null,this.distcode=null,this.lenbits=0,this.distbits=0,this.ncode=0,this.nlen=0,this.ndist=0,this.have=0,this.next=null,this.lens=new Uint16Array(320),this.work=new Uint16Array(288),this.lendyn=null,this.distdyn=null,this.sane=0,this.back=0,this.was=0}const we=t=>{if(!t||!t.state)return le;const e=t.state;return t.total_in=t.total_out=e.total=0,t.msg="",e.wrap&&(t.adler=1&e.wrap),e.mode=1,e.last=0,e.havedict=0,e.dmax=32768,e.head=null,e.hold=0,e.bits=0,e.lencode=e.lendyn=new Int32Array(852),e.distcode=e.distdyn=new Int32Array(592),e.sane=1,e.back=-1,se},be=t=>{if(!t||!t.state)return le;const e=t.state;return e.wsize=0,e.whave=0,e.wnext=0,we(t)},ge=(t,e)=>{let a;if(!t||!t.state)return le;const i=t.state;return e<0?(a=0,e=-e):(a=1+(e>>4),e<48&&(e&=15)),e&&(e<8||e>15)?le:(null!==i.window&&i.wbits!==e&&(i.window=null),i.wrap=a,i.wbits=e,be(t))},pe=(t,e)=>{if(!t)return le;const a=new ue;t.state=a,a.window=null;const i=ge(t,e);return i!==se&&(t.state=null),i};let me,ke,ve=!0;const ye=t=>{if(ve){me=new Int32Array(512),ke=new Int32Array(32);let e=0;for(;e<144;)t.lens[e++]=8;for(;e<256;)t.lens[e++]=9;for(;e<280;)t.lens[e++]=7;for(;e<288;)t.lens[e++]=8;for(ee(1,t.lens,0,288,me,0,t.work,{bits:9}),e=0;e<32;)t.lens[e++]=5;ee(2,t.lens,0,32,ke,0,t.work,{bits:5}),ve=!1}t.lencode=me,t.lenbits=9,t.distcode=ke,t.distbits=5},xe=(t,e,a,i)=>{let n;const s=t.state;return null===s.window&&(s.wsize=1<<s.wbits,s.wnext=0,s.whave=0,s.window=new Uint8Array(s.wsize)),i>=s.wsize?(s.window.set(e.subarray(a-s.wsize,a),0),s.wnext=0,s.whave=s.wsize):(n=s.wsize-s.wnext,n>i&&(n=i),s.window.set(e.subarray(a-i,a-i+n),s.wnext),(i-=n)?(s.window.set(e.subarray(a-i,a),0),s.wnext=i,s.whave=s.wsize):(s.wnext+=n,s.wnext===s.wsize&&(s.wnext=0),s.whave<s.wsize&&(s.whave+=n))),0};var ze={inflateReset:be,inflateReset2:ge,inflateResetKeep:we,inflateInit:t=>pe(t,15),inflateInit2:pe,inflate:(t,e)=>{let a,i,n,s,r,o,l,h,d,_,f,c,u,w,b,g,p,m,k,v,y,x,z=0;const A=new Uint8Array(4);let E,R;const Z=new Uint8Array([16,17,18,0,8,7,9,6,10,5,11,4,12,3,13,2,14,1,15]);if(!t||!t.state||!t.output||!t.input&&0!==t.avail_in)return le;a=t.state,12===a.mode&&(a.mode=13),r=t.next_out,n=t.output,l=t.avail_out,s=t.next_in,i=t.input,o=t.avail_in,h=a.hold,d=a.bits,_=o,f=l,x=se;t:for(;;)switch(a.mode){case 1:if(0===a.wrap){a.mode=13;break}for(;d<16;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}if(2&a.wrap&&35615===h){a.check=0,A[0]=255&h,A[1]=h>>>8&255,a.check=L(a.check,A,2,0),h=0,d=0,a.mode=2;break}if(a.flags=0,a.head&&(a.head.done=!1),!(1&a.wrap)||(((255&h)<<8)+(h>>8))%31){t.msg="incorrect header check",a.mode=30;break}if((15&h)!==fe){t.msg="unknown compression method",a.mode=30;break}if(h>>>=4,d-=4,y=8+(15&h),0===a.wbits)a.wbits=y;else if(y>a.wbits){t.msg="invalid window size",a.mode=30;break}a.dmax=1<<a.wbits,t.adler=a.check=1,a.mode=512&h?10:12,h=0,d=0;break;case 2:for(;d<16;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}if(a.flags=h,(255&a.flags)!==fe){t.msg="unknown compression method",a.mode=30;break}if(57344&a.flags){t.msg="unknown header flags set",a.mode=30;break}a.head&&(a.head.text=h>>8&1),512&a.flags&&(A[0]=255&h,A[1]=h>>>8&255,a.check=L(a.check,A,2,0)),h=0,d=0,a.mode=3;case 3:for(;d<32;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}a.head&&(a.head.time=h),512&a.flags&&(A[0]=255&h,A[1]=h>>>8&255,A[2]=h>>>16&255,A[3]=h>>>24&255,a.check=L(a.check,A,4,0)),h=0,d=0,a.mode=4;case 4:for(;d<16;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}a.head&&(a.head.xflags=255&h,a.head.os=h>>8),512&a.flags&&(A[0]=255&h,A[1]=h>>>8&255,a.check=L(a.check,A,2,0)),h=0,d=0,a.mode=5;case 5:if(1024&a.flags){for(;d<16;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}a.length=h,a.head&&(a.head.extra_len=h),512&a.flags&&(A[0]=255&h,A[1]=h>>>8&255,a.check=L(a.check,A,2,0)),h=0,d=0}else a.head&&(a.head.extra=null);a.mode=6;case 6:if(1024&a.flags&&(c=a.length,c>o&&(c=o),c&&(a.head&&(y=a.head.extra_len-a.length,a.head.extra||(a.head.extra=new Uint8Array(a.head.extra_len)),a.head.extra.set(i.subarray(s,s+c),y)),512&a.flags&&(a.check=L(a.check,i,c,s)),o-=c,s+=c,a.length-=c),a.length))break t;a.length=0,a.mode=7;case 7:if(2048&a.flags){if(0===o)break t;c=0;do{y=i[s+c++],a.head&&y&&a.length<65536&&(a.head.name+=String.fromCharCode(y))}while(y&&c<o);if(512&a.flags&&(a.check=L(a.check,i,c,s)),o-=c,s+=c,y)break t}else a.head&&(a.head.name=null);a.length=0,a.mode=8;case 8:if(4096&a.flags){if(0===o)break t;c=0;do{y=i[s+c++],a.head&&y&&a.length<65536&&(a.head.comment+=String.fromCharCode(y))}while(y&&c<o);if(512&a.flags&&(a.check=L(a.check,i,c,s)),o-=c,s+=c,y)break t}else a.head&&(a.head.comment=null);a.mode=9;case 9:if(512&a.flags){for(;d<16;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}if(h!==(65535&a.check)){t.msg="header crc mismatch",a.mode=30;break}h=0,d=0}a.head&&(a.head.hcrc=a.flags>>9&1,a.head.done=!0),t.adler=a.check=0,a.mode=12;break;case 10:for(;d<32;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}t.adler=a.check=ce(h),h=0,d=0,a.mode=11;case 11:if(0===a.havedict)return t.next_out=r,t.avail_out=l,t.next_in=s,t.avail_in=o,a.hold=h,a.bits=d,oe;t.adler=a.check=1,a.mode=12;case 12:if(e===ie||e===ne)break t;case 13:if(a.last){h>>>=7&d,d-=7&d,a.mode=27;break}for(;d<3;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}switch(a.last=1&h,h>>>=1,d-=1,3&h){case 0:a.mode=14;break;case 1:if(ye(a),a.mode=20,e===ne){h>>>=2,d-=2;break t}break;case 2:a.mode=17;break;case 3:t.msg="invalid block type",a.mode=30}h>>>=2,d-=2;break;case 14:for(h>>>=7&d,d-=7&d;d<32;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}if((65535&h)!=(h>>>16^65535)){t.msg="invalid stored block lengths",a.mode=30;break}if(a.length=65535&h,h=0,d=0,a.mode=15,e===ne)break t;case 15:a.mode=16;case 16:if(c=a.length,c){if(c>o&&(c=o),c>l&&(c=l),0===c)break t;n.set(i.subarray(s,s+c),r),o-=c,s+=c,l-=c,r+=c,a.length-=c;break}a.mode=12;break;case 17:for(;d<14;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}if(a.nlen=257+(31&h),h>>>=5,d-=5,a.ndist=1+(31&h),h>>>=5,d-=5,a.ncode=4+(15&h),h>>>=4,d-=4,a.nlen>286||a.ndist>30){t.msg="too many length or distance symbols",a.mode=30;break}a.have=0,a.mode=18;case 18:for(;a.have<a.ncode;){for(;d<3;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}a.lens[Z[a.have++]]=7&h,h>>>=3,d-=3}for(;a.have<19;)a.lens[Z[a.have++]]=0;if(a.lencode=a.lendyn,a.lenbits=7,E={bits:a.lenbits},x=ee(0,a.lens,0,19,a.lencode,0,a.work,E),a.lenbits=E.bits,x){t.msg="invalid code lengths set",a.mode=30;break}a.have=0,a.mode=19;case 19:for(;a.have<a.nlen+a.ndist;){for(;z=a.lencode[h&(1<<a.lenbits)-1],b=z>>>24,g=z>>>16&255,p=65535&z,!(b<=d);){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}if(p<16)h>>>=b,d-=b,a.lens[a.have++]=p;else{if(16===p){for(R=b+2;d<R;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}if(h>>>=b,d-=b,0===a.have){t.msg="invalid bit length repeat",a.mode=30;break}y=a.lens[a.have-1],c=3+(3&h),h>>>=2,d-=2}else if(17===p){for(R=b+3;d<R;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}h>>>=b,d-=b,y=0,c=3+(7&h),h>>>=3,d-=3}else{for(R=b+7;d<R;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}h>>>=b,d-=b,y=0,c=11+(127&h),h>>>=7,d-=7}if(a.have+c>a.nlen+a.ndist){t.msg="invalid bit length repeat",a.mode=30;break}for(;c--;)a.lens[a.have++]=y}}if(30===a.mode)break;if(0===a.lens[256]){t.msg="invalid code -- missing end-of-block",a.mode=30;break}if(a.lenbits=9,E={bits:a.lenbits},x=ee(1,a.lens,0,a.nlen,a.lencode,0,a.work,E),a.lenbits=E.bits,x){t.msg="invalid literal/lengths set",a.mode=30;break}if(a.distbits=6,a.distcode=a.distdyn,E={bits:a.distbits},x=ee(2,a.lens,a.nlen,a.ndist,a.distcode,0,a.work,E),a.distbits=E.bits,x){t.msg="invalid distances set",a.mode=30;break}if(a.mode=20,e===ne)break t;case 20:a.mode=21;case 21:if(o>=6&&l>=258){t.next_out=r,t.avail_out=l,t.next_in=s,t.avail_in=o,a.hold=h,a.bits=d,qt(t,f),r=t.next_out,n=t.output,l=t.avail_out,s=t.next_in,i=t.input,o=t.avail_in,h=a.hold,d=a.bits,12===a.mode&&(a.back=-1);break}for(a.back=0;z=a.lencode[h&(1<<a.lenbits)-1],b=z>>>24,g=z>>>16&255,p=65535&z,!(b<=d);){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}if(g&&0==(240&g)){for(m=b,k=g,v=p;z=a.lencode[v+((h&(1<<m+k)-1)>>m)],b=z>>>24,g=z>>>16&255,p=65535&z,!(m+b<=d);){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}h>>>=m,d-=m,a.back+=m}if(h>>>=b,d-=b,a.back+=b,a.length=p,0===g){a.mode=26;break}if(32&g){a.back=-1,a.mode=12;break}if(64&g){t.msg="invalid literal/length code",a.mode=30;break}a.extra=15&g,a.mode=22;case 22:if(a.extra){for(R=a.extra;d<R;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}a.length+=h&(1<<a.extra)-1,h>>>=a.extra,d-=a.extra,a.back+=a.extra}a.was=a.length,a.mode=23;case 23:for(;z=a.distcode[h&(1<<a.distbits)-1],b=z>>>24,g=z>>>16&255,p=65535&z,!(b<=d);){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}if(0==(240&g)){for(m=b,k=g,v=p;z=a.distcode[v+((h&(1<<m+k)-1)>>m)],b=z>>>24,g=z>>>16&255,p=65535&z,!(m+b<=d);){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}h>>>=m,d-=m,a.back+=m}if(h>>>=b,d-=b,a.back+=b,64&g){t.msg="invalid distance code",a.mode=30;break}a.offset=p,a.extra=15&g,a.mode=24;case 24:if(a.extra){for(R=a.extra;d<R;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}a.offset+=h&(1<<a.extra)-1,h>>>=a.extra,d-=a.extra,a.back+=a.extra}if(a.offset>a.dmax){t.msg="invalid distance too far back",a.mode=30;break}a.mode=25;case 25:if(0===l)break t;if(c=f-l,a.offset>c){if(c=a.offset-c,c>a.whave&&a.sane){t.msg="invalid distance too far back",a.mode=30;break}c>a.wnext?(c-=a.wnext,u=a.wsize-c):u=a.wnext-c,c>a.length&&(c=a.length),w=a.window}else w=n,u=r-a.offset,c=a.length;c>l&&(c=l),l-=c,a.length-=c;do{n[r++]=w[u++]}while(--c);0===a.length&&(a.mode=21);break;case 26:if(0===l)break t;n[r++]=a.length,l--,a.mode=21;break;case 27:if(a.wrap){for(;d<32;){if(0===o)break t;o--,h|=i[s++]<<d,d+=8}if(f-=l,t.total_out+=f,a.total+=f,f&&(t.adler=a.check=a.flags?L(a.check,n,f,r-f):N(a.check,n,f,r-f)),f=l,(a.flags?h:ce(h))!==a.check){t.msg="incorrect data check",a.mode=30;break}h=0,d=0}a.mode=28;case 28:if(a.wrap&&a.flags){for(;d<32;){if(0===o)break t;o--,h+=i[s++]<<d,d+=8}if(h!==(4294967295&a.total)){t.msg="incorrect length check",a.mode=30;break}h=0,d=0}a.mode=29;case 29:x=re;break t;case 30:x=he;break t;case 31:return de;default:return le}return t.next_out=r,t.avail_out=l,t.next_in=s,t.avail_in=o,a.hold=h,a.bits=d,(a.wsize||f!==t.avail_out&&a.mode<30&&(a.mode<27||e!==ae))&&xe(t,t.output,t.next_out,f-t.avail_out),_-=t.avail_in,f-=t.avail_out,t.total_in+=_,t.total_out+=f,a.total+=f,a.wrap&&f&&(t.adler=a.check=a.flags?L(a.check,n,f,t.next_out-f):N(a.check,n,f,t.next_out-f)),t.data_type=a.bits+(a.last?64:0)+(12===a.mode?128:0)+(20===a.mode||15===a.mode?256:0),(0===_&&0===f||e===ae)&&x===se&&(x=_e),x},inflateEnd:t=>{if(!t||!t.state)return le;let e=t.state;return e.window&&(e.window=null),t.state=null,se},inflateGetHeader:(t,e)=>{if(!t||!t.state)return le;const a=t.state;return 0==(2&a.wrap)?le:(a.head=e,e.done=!1,se)},inflateSetDictionary:(t,e)=>{const a=e.length;let i,n,s;return t&&t.state?(i=t.state,0!==i.wrap&&11!==i.mode?le:11===i.mode&&(n=1,n=N(n,e,a,0),n!==i.check)?he:(s=xe(t,e,a,a),s?(i.mode=31,de):(i.havedict=1,se))):le},inflateInfo:"pako inflate (from Nodeca project)"};var Ae=function(){this.text=0,this.time=0,this.xflags=0,this.os=0,this.extra=null,this.extra_len=0,this.name="",this.comment="",this.hcrc=0,this.done=!1};const Ee=Object.prototype.toString,{Z_NO_FLUSH:Re,Z_FINISH:Ze,Z_OK:Se,Z_STREAM_END:Ue,Z_NEED_DICT:De,Z_STREAM_ERROR:Oe,Z_DATA_ERROR:Te,Z_MEM_ERROR:Ne}=B;function Fe(t){this.options=St({chunkSize:65536,windowBits:15,to:""},t||{});const e=this.options;e.raw&&e.windowBits>=0&&e.windowBits<16&&(e.windowBits=-e.windowBits,0===e.windowBits&&(e.windowBits=-15)),!(e.windowBits>=0&&e.windowBits<16)||t&&t.windowBits||(e.windowBits+=32),e.windowBits>15&&e.windowBits<48&&0==(15&e.windowBits)&&(e.windowBits|=15),this.err=0,this.msg="",this.ended=!1,this.chunks=[],this.strm=new Lt,this.strm.avail_out=0;let a=ze.inflateInit2(this.strm,e.windowBits);if(a!==Se)throw new Error(I[a]);if(this.header=new Ae,ze.inflateGetHeader(this.strm,this.header),e.dictionary&&("string"==typeof e.dictionary?e.dictionary=Tt(e.dictionary):"[object ArrayBuffer]"===Ee.call(e.dictionary)&&(e.dictionary=new Uint8Array(e.dictionary)),e.raw&&(a=ze.inflateSetDictionary(this.strm,e.dictionary),a!==Se)))throw new Error(I[a])}Fe.prototype.push=function(t,e){const a=this.strm,i=this.options.chunkSize,n=this.options.dictionary;let s,r,o;if(this.ended)return!1;for(r=e===~~e?e:!0===e?Ze:Re,"[object ArrayBuffer]"===Ee.call(t)?a.input=new Uint8Array(t):a.input=t,a.next_in=0,a.avail_in=a.input.length;;){for(0===a.avail_out&&(a.output=new Uint8Array(i),a.next_out=0,a.avail_out=i),s=ze.inflate(a,r),s===De&&n&&(s=ze.inflateSetDictionary(a,n),s===Se?s=ze.inflate(a,r):s===Te&&(s=De));a.avail_in>0&&s===Ue&&a.state.wrap>0&&0!==t[a.next_in];)ze.inflateReset(a),s=ze.inflate(a,r);switch(s){case Oe:case Te:case De:case Ne:return this.onEnd(s),this.ended=!0,!1}if(o=a.avail_out,a.next_out&&(0===a.avail_out||s===Ue))if("string"===this.options.to){let t=Ft(a.output,a.next_out),e=a.next_out-t,n=Nt(a.output,t);a.next_out=e,a.avail_out=i-e,e&&a.output.set(a.output.subarray(t,t+e),0),this.onData(n)}else this.onData(a.output.length===a.next_out?a.output:a.output.subarray(0,a.next_out));if(s!==Se||0!==o){if(s===Ue)return s=ze.inflateEnd(this.strm),this.onEnd(s),this.ended=!0,!0;if(0===a.avail_in)break}}return!0},Fe.prototype.onData=function(t){this.chunks.push(t)},Fe.prototype.onEnd=function(t){t===Se&&("string"===this.options.to?this.result=this.chunks.join(""):this.result=Ut(this.chunks)),this.chunks=[],this.err=t,this.msg=this.strm.msg};const{Deflate:Le,deflate:Ie,deflateRaw:Be,gzip:Ce}=Wt;var He=Le,Me=B,je=new(function(){function t(){this.init()}return t.prototype.init=function(){this.added=0,this.deflate=new He,this.deflate.push("[",Me.Z_NO_FLUSH)},t.prototype.addEvent=function(t){if(t){var e=this.added>0?",":"";this.deflate.push(e+JSON.stringify(t),Me.Z_NO_FLUSH),this.added++}},t.prototype.finish=function(){if(this.deflate.push("]",Me.Z_FINISH),this.deflate.err)throw this.deflate.err;var t=this.deflate.result;return this.init(),t},t}()),Ke={init:function(){return je.init(),""},addEvent:function(t){return je.addEvent(t),""},finish:function(){return je.finish()}};addEventListener("message",(function(t){var e=t.data.method,a=t.data.id,i=(t.data.args?JSON.parse(t.data.args):[])[0];if(e in Ke&&"function"==typeof Ke[e])try{var n=Ke[e](i);postMessage({id:a,method:e,success:!0,response:n})}catch(t){postMessage({id:a,method:e,success:!1,response:t}),console.error(t)}}));`;

function createEventBuffer(_a) {
    var useCompression = _a.useCompression;
    if (useCompression && window.Worker) {
        var workerBlob = new Blob([workerString]);
        var workerUrl = URL.createObjectURL(workerBlob);
        try {
            logger.log('Using compression worker');
            var worker = new Worker(workerUrl);
            if (worker) {
                return new EventBufferCompressionWorker(worker);
            }
            else {
                captureException(new Error('Unable to create compression worker'));
            }
        }
        catch (_b) {
            // catch and ignore, fallback to simple event buffer
        }
        logger.log('Falling back to simple event buffer');
    }
    logger.log('Using simple buffer');
    return new EventBufferArray();
}
var EventBufferArray = /** @class */ (function () {
    function EventBufferArray() {
        this.events = [];
    }
    EventBufferArray.prototype.destroy = function () {
        this.events = [];
    };
    Object.defineProperty(EventBufferArray.prototype, "length", {
        get: function () {
            return this.events.length;
        },
        enumerable: false,
        configurable: true
    });
    EventBufferArray.prototype.addEvent = function (event, isCheckout) {
        if (isCheckout) {
            this.events = [event];
            return;
        }
        this.events.push(event);
    };
    EventBufferArray.prototype.finish = function () {
        var _this = this;
        return new Promise(function (resolve) {
            // Make a copy of the events array reference and immediately clear the
            // events member so that we do not lose new events while uploading
            // attachment.
            var eventsRet = _this.events;
            _this.events = [];
            resolve(JSON.stringify(eventsRet));
        });
    };
    return EventBufferArray;
}());
// exporting for testing
var EventBufferCompressionWorker = /** @class */ (function () {
    function EventBufferCompressionWorker(worker) {
        var _this = this;
        this.eventBufferItemLength = 0;
        this._id = 0;
        this.sendEventToWorker = function (event) {
            var promise = _this.postMessage({
                id: _this.id,
                method: 'addEvent',
                args: [event],
            });
            // XXX: See note in `get length()`
            _this.eventBufferItemLength++;
            return promise;
        };
        this.finishRequest = function (id) { return __awaiter(_this, void 0, void 0, function () {
            var promise;
            return __generator(this, function (_a) {
                promise = this.postMessage({ id: id, method: 'finish', args: [] });
                // XXX: See note in `get length()`
                this.eventBufferItemLength = 0;
                return [2 /*return*/, promise];
            });
        }); };
        this.worker = worker;
    }
    Object.defineProperty(EventBufferCompressionWorker.prototype, "id", {
        /**
         * Read-only incrementing counter
         */
        get: function () {
            return this._id++;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Post message to worker and wait for response before resolving promise.
     */
    EventBufferCompressionWorker.prototype.postMessage = function (_a) {
        var _this = this;
        var id = _a.id, method = _a.method, args = _a.args;
        return new Promise(function (resolve, reject) {
            var _a, _b;
            var listener = function (_a) {
                var _b;
                var data = _a.data;
                if (data.method !== method) {
                    return;
                }
                // There can be multiple listeners for a single method, the id ensures
                // that the response matches the caller.
                if (data.id !== id) {
                    return;
                }
                // At this point, we'll always want to remove listener regardless of result status
                (_b = _this.worker) === null || _b === void 0 ? void 0 : _b.removeEventListener('message', listener);
                if (!data.success) {
                    // TODO: Do some error handling, not sure what
                    logger.error(data.response);
                    reject(new Error('Error in compression worker'));
                    return;
                }
                resolve(data.response);
            };
            var stringifiedArgs;
            try {
                stringifiedArgs = JSON.stringify(args);
            }
            catch (err) {
                console.error(err);
                stringifiedArgs = '[]';
            }
            // Note: we can't use `once` option because it's possible it needs to
            // listen to multiple messages
            (_a = _this.worker) === null || _a === void 0 ? void 0 : _a.addEventListener('message', listener);
            (_b = _this.worker) === null || _b === void 0 ? void 0 : _b.postMessage({ id: id, method: method, args: stringifiedArgs });
        });
    };
    EventBufferCompressionWorker.prototype.init = function () {
        this.postMessage({ id: this.id, method: 'init', args: [] });
        logger.log('Initialized compression worker');
    };
    EventBufferCompressionWorker.prototype.destroy = function () {
        var _a;
        logger.log('Destroying compression worker');
        (_a = this.worker) === null || _a === void 0 ? void 0 : _a.terminate();
        this.worker = null;
    };
    Object.defineProperty(EventBufferCompressionWorker.prototype, "length", {
        /**
         * Note that this may not reflect what is actually in the event buffer. This
         * is only a local count of the buffer size since `addEvent` is async.
         */
        get: function () {
            return this.eventBufferItemLength;
        },
        enumerable: false,
        configurable: true
    });
    EventBufferCompressionWorker.prototype.addEvent = function (event, isCheckout) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!isCheckout) return [3 /*break*/, 2];
                        // This event is a checkout, make sure worker buffer is cleared before
                        // proceeding.
                        return [4 /*yield*/, this.postMessage({
                                id: this.id,
                                method: 'init',
                                args: [],
                            })];
                    case 1:
                        // This event is a checkout, make sure worker buffer is cleared before
                        // proceeding.
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/, this.sendEventToWorker(event)];
                }
            });
        });
    };
    EventBufferCompressionWorker.prototype.finish = function () {
        return this.finishRequest(this.id);
    };
    return EventBufferCompressionWorker;
}());

var BASE_RETRY_INTERVAL = 5000;
var MAX_RETRY_COUNT = 3;
var UNABLE_TO_SEND_REPLAY = 'Unable to send Replay';
var MEDIA_SELECTORS = 'img,image,svg,path,rect,area,video,object,picture,embed,map,audio';
var _initialized = false;
var isBrowser = typeof window !== 'undefined';
var Replay = /** @class */ (function () {
    function Replay(_a) {
        if (_a === void 0) { _a = {}; }
        var _this = this;
        var _b = _a.flushMinDelay, flushMinDelay = _b === void 0 ? 5000 : _b, _c = _a.flushMaxDelay, flushMaxDelay = _c === void 0 ? 15000 : _c, _d = _a.initialFlushDelay, initialFlushDelay = _d === void 0 ? 5000 : _d, _e = _a.stickySession, stickySession = _e === void 0 ? true : _e, _f = _a.useCompression, useCompression = _f === void 0 ? true : _f, _g = _a.sessionSampleRate, sessionSampleRate = _g === void 0 ? DEFAULT_SESSION_SAMPLE_RATE : _g, _h = _a.errorSampleRate, errorSampleRate = _h === void 0 ? DEFAULT_ERROR_SAMPLE_RATE : _h, _j = _a.maskAllText, maskAllText = _j === void 0 ? true : _j, _k = _a.maskAllInputs, maskAllInputs = _k === void 0 ? true : _k, _l = _a.blockAllMedia, blockAllMedia = _l === void 0 ? true : _l, _m = _a.blockClass, blockClass = _m === void 0 ? 'sentry-block' : _m, _o = _a.ignoreClass, ignoreClass = _o === void 0 ? 'sentry-ignore' : _o, _p = _a.maskTextClass, maskTextClass = _p === void 0 ? 'sentry-mask' : _p, _q = _a.blockSelector, blockSelector = _q === void 0 ? '[data-sentry-block]' : _q, replaysSamplingRate = _a.replaysSamplingRate, captureOnlyOnError = _a.captureOnlyOnError, recordingOptions = __rest(_a, ["flushMinDelay", "flushMaxDelay", "initialFlushDelay", "stickySession", "useCompression", "sessionSampleRate", "errorSampleRate", "maskAllText", "maskAllInputs", "blockAllMedia", "blockClass", "ignoreClass", "maskTextClass", "blockSelector", "replaysSamplingRate", "captureOnlyOnError"]);
        /**
         * @inheritDoc
         */
        this.name = Replay.id;
        /**
         * List of PerformanceEntry from PerformanceObserver
         */
        this.performanceEvents = [];
        this.performanceObserver = null;
        this.retryCount = 0;
        this.retryInterval = BASE_RETRY_INTERVAL;
        this.flushLock = null;
        /**
         * Timestamp of the last user activity. This lives across sessions.
         */
        this.lastActivity = new Date().getTime();
        /**
         * Is the integration currently active?
         */
        this.isEnabled = false;
        /**
         * Paused is a state where:
         * - DOM Recording is not listening at all
         * - Nothing will be added to event buffer (e.g. core SDK events)
         */
        this.isPaused = false;
        /**
         * Integration will wait until an error occurs before creating and sending a
         * replay.
         */
        this.waitForError = false;
        /**
         * Have we attached listeners to the core SDK?
         * Note we have to track this as there is no way to remove instrumentation handlers.
         */
        this.hasInitializedCoreListeners = false;
        /**
         * Function to stop recording
         */
        this.stopRecording = null;
        this.context = {
            errorIds: new Set(),
            traceIds: new Set(),
            urls: [],
            earliestEvent: null,
            initialTimestamp: new Date().getTime(),
            initialUrl: '',
        };
        /**
         * Core Sentry SDK global event handler. Attaches `replayId` to all [non-replay]
         * events as a tag. Also handles the case where we only want to capture a reply
         * when an error occurs.
         **/
        this.handleGlobalEvent = function (event) {
            var _a, _b, _c, _d, _e;
            // Do not apply replayId to the root event
            if (
            // @ts-expect-error new event type
            event.type === REPLAY_EVENT_NAME) {
                // Replays have separate set of breadcrumbs, do not include breadcrumbs
                // from core SDK
                delete event.breadcrumbs;
                return event;
            }
            // Only tag transactions with replayId if not waiting for an error
            if (event.type !== 'transaction' || !_this.waitForError) {
                event.tags = __assign(__assign({}, event.tags), { replayId: (_a = _this.session) === null || _a === void 0 ? void 0 : _a.id });
            }
            // Collect traceIds in context regardless of `waitForError` - if it's true,
            // context gets cleared on every checkout
            if (event.type === 'transaction') {
                _this.context.traceIds.add(String(((_c = (_b = event.contexts) === null || _b === void 0 ? void 0 : _b.trace) === null || _c === void 0 ? void 0 : _c.trace_id) || ''));
                return event;
            }
            // XXX: Is it safe to assume that all other events are error events?
            // @ts-expect-error: Type 'undefined' is not assignable to type 'string'.ts(2345)
            _this.context.errorIds.add(event.event_id);
            var exc = (_e = (_d = event.exception) === null || _d === void 0 ? void 0 : _d.values) === null || _e === void 0 ? void 0 : _e[0];
            addInternalBreadcrumb({
                message: "Tagging event (".concat(event.event_id, ") - ").concat(event.message, " - ").concat((exc === null || exc === void 0 ? void 0 : exc.type) || 'Unknown', ": ").concat((exc === null || exc === void 0 ? void 0 : exc.value) || 'n/a'),
            });
            // Need to be very careful that this does not cause an infinite loop
            if (_this.waitForError &&
                event.exception &&
                event.message !== UNABLE_TO_SEND_REPLAY // ignore this error because otherwise we could loop indefinitely with trying to capture replay and failing
            ) {
                setTimeout(function () { return __awaiter(_this, void 0, void 0, function () {
                    return __generator(this, function (_a) {
                        switch (_a.label) {
                            case 0: 
                            // Allow flush to complete before resuming as a session recording, otherwise
                            // the checkout from `startRecording` may be included in the payload.
                            // Prefer to keep the error replay as a separate (and smaller) segment
                            // than the session replay.
                            return [4 /*yield*/, this.flushImmediate()];
                            case 1:
                                // Allow flush to complete before resuming as a session recording, otherwise
                                // the checkout from `startRecording` may be included in the payload.
                                // Prefer to keep the error replay as a separate (and smaller) segment
                                // than the session replay.
                                _a.sent();
                                if (this.stopRecording) {
                                    this.stopRecording();
                                    // Reset all "capture on error" configuration before
                                    // starting a new recording
                                    delete this.recordingOptions.checkoutEveryNms;
                                    this.waitForError = false;
                                    this.startRecording();
                                }
                                return [2 /*return*/];
                        }
                    });
                }); });
            }
            return event;
        };
        /**
         * Handler for recording events.
         *
         * Adds to event buffer, and has varying flushing behaviors if the event was a checkout.
         */
        this.handleRecordingEmit = function (event, isCheckout) {
            // If this is false, it means session is expired, create and a new session and wait for checkout
            if (!_this.checkAndHandleExpiredSession()) {
                logger.error(new Error('Received replay event after session expired.'));
                return;
            }
            _this.addUpdate(function () {
                var _a, _b;
                // The session is always started immediately on pageload/init, but for
                // error-only replays, it should reflect the most recent checkout
                // when an error occurs. Clear any state that happens before this current
                // checkout. This needs to happen before `addEvent()` which updates state
                // dependent on this reset.
                if (_this.waitForError && event.type === 2) {
                    _this.setInitialState();
                }
                // We need to clear existing events on a checkout, otherwise they are
                // incremental event updates and should be appended
                _this.addEvent(event, isCheckout);
                // Different behavior for full snapshots (type=2), ignore other event types
                // See https://github.com/rrweb-io/rrweb/blob/d8f9290ca496712aa1e7d472549480c4e7876594/packages/rrweb/src/types.ts#L16
                if (event.type !== 2) {
                    return false;
                }
                // If there is a previousSessionId after a full snapshot occurs, then
                // the replay session was started due to session expiration. The new session
                // is started before triggering a new checkout and contains the id
                // of the previous session. Do not immediately flush in this case
                // to avoid capturing only the checkout and instead the replay will
                // be captured if they perform any follow-up actions.
                if ((_a = _this.session) === null || _a === void 0 ? void 0 : _a.previousSessionId) {
                    return true;
                }
                // See note above re: session start needs to reflect the most recent
                // checkout.
                if (_this.waitForError && _this.session && _this.context.earliestEvent) {
                    _this.session.started = _this.context.earliestEvent;
                }
                // If the full snapshot is due to an initial load, we will not have
                // a previous session ID. In this case, we want to buffer events
                // for a set amount of time before flushing. This can help avoid
                // capturing replays of users that immediately close the window.
                setTimeout(function () { return _this.conditionalFlush(); }, _this.options.initialFlushDelay);
                // Cancel any previously debounced flushes to ensure there are no [near]
                // simultaneous flushes happening. The latter request should be
                // insignificant in this case, so wait for additional user interaction to
                // trigger a new flush.
                //
                // This can happen because there's no guarantee that a recording event
                // happens first. e.g. a mouse click can happen and trigger a debounced
                // flush before the checkout.
                (_b = _this.debouncedFlush) === null || _b === void 0 ? void 0 : _b.cancel();
                return true;
            });
        };
        /**
         * Handle when visibility of the page content changes. Opening a new tab will
         * cause the state to change to hidden because of content of current page will
         * be hidden. Likewise, moving a different window to cover the contents of the
         * page will also trigger a change to a hidden state.
         */
        this.handleVisibilityChange = function () {
            if (document.visibilityState === 'visible') {
                _this.doChangeToForegroundTasks();
            }
            else {
                _this.doChangeToBackgroundTasks();
            }
        };
        /**
         * Handle when page is blurred
         */
        this.handleWindowBlur = function () {
            var breadcrumb = createBreadcrumb({
                category: 'ui.blur',
            });
            // Do not count blur as a user action -- it's part of the process of them
            // leaving the page
            _this.doChangeToBackgroundTasks(breadcrumb);
        };
        /**
         * Handle when page is focused
         */
        this.handleWindowFocus = function () {
            var breadcrumb = createBreadcrumb({
                category: 'ui.focus',
            });
            // Do not count focus as a user action -- instead wait until they focus and
            // interactive with page
            _this.doChangeToForegroundTasks(breadcrumb);
        };
        /**
         * Handler for Sentry Core SDK events.
         *
         * These specific events will create span-like objects in the recording.
         */
        this.handleCoreSpanListener = function (type) { return function (handlerData) {
            if (!_this.isEnabled) {
                return;
            }
            var handler = getSpanHandler(type);
            var result = handler(handlerData);
            if (result === null) {
                return;
            }
            if (type === 'history') {
                // Need to collect visited URLs
                _this.context.urls.push(result.name);
                _this.triggerUserActivity();
            }
            _this.addUpdate(function () {
                _this.createPerformanceSpans([result]);
                // Returning true will cause `addUpdate` to not flush
                // We do not want network requests to cause a flush. This will prevent
                // recurring/polling requests from keeping the replay session alive.
                return ['xhr', 'fetch'].includes(type);
            });
        }; };
        /**
         * Handler for Sentry Core SDK events.
         *
         * These events will create breadcrumb-like objects in the recording.
         */
        this.handleCoreBreadcrumbListener = function (type) { return function (handlerData) {
            if (!_this.isEnabled) {
                return;
            }
            var handler = getBreadcrumbHandler(type);
            var result = handler(handlerData);
            if (result === null) {
                return;
            }
            if (result.category === 'sentry.transaction') {
                return;
            }
            if (result.category === 'ui.click') {
                _this.triggerUserActivity();
            }
            else {
                _this.checkAndHandleExpiredSession();
            }
            _this.addUpdate(function () {
                _this.addEvent({
                    type: EventType.Custom,
                    // TODO: We were converting from ms to seconds for breadcrumbs, spans,
                    // but maybe we should just keep them as milliseconds
                    timestamp: (result.timestamp || 0) * 1000,
                    data: {
                        tag: 'breadcrumb',
                        payload: result,
                    },
                });
                // Do not flush after console log messages
                return result.category === 'console';
            });
        }; };
        /**
         * Keep a list of performance entries that will be sent with a replay
         */
        this.handlePerformanceObserver = function (list) {
            // For whatever reason the observer was returning duplicate navigation
            // entries (the other entry types were not duplicated).
            var newPerformanceEntries = dedupePerformanceEntries(_this.performanceEvents, list.getEntries());
            _this.performanceEvents = newPerformanceEntries;
        };
        /**
         * Flush recording data to Sentry. Creates a lock so that only a single flush
         * can be active at a time. Do not call this directly.
         */
        this.flush = function () { return __awaiter(_this, void 0, void 0, function () {
            var err_1;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        if (!this.isEnabled) {
                            // This is just a precaution, there should be no listeners that would
                            // cause a flush.
                            return [2 /*return*/];
                        }
                        if (!this.checkAndHandleExpiredSession()) {
                            logger.error(new Error('Attempting to finish replay event after session expired.'));
                            return [2 /*return*/];
                        }
                        if (!((_a = this.session) === null || _a === void 0 ? void 0 : _a.id)) {
                            console.error(new Error('[Sentry]: No transaction, no replay'));
                            return [2 /*return*/];
                        }
                        // A flush is about to happen, cancel any queued flushes
                        (_b = this.debouncedFlush) === null || _b === void 0 ? void 0 : _b.cancel();
                        if (!!this.flushLock) return [3 /*break*/, 2];
                        this.flushLock = this.runFlush();
                        return [4 /*yield*/, this.flushLock];
                    case 1:
                        _c.sent();
                        this.flushLock = null;
                        return [2 /*return*/];
                    case 2:
                        _c.trys.push([2, 4, 5, 6]);
                        return [4 /*yield*/, this.flushLock];
                    case 3:
                        _c.sent();
                        return [3 /*break*/, 6];
                    case 4:
                        err_1 = _c.sent();
                        console.error(err_1);
                        return [3 /*break*/, 6];
                    case 5:
                        this.debouncedFlush();
                        return [7 /*endfinally*/];
                    case 6: return [2 /*return*/];
                }
            });
        }); };
        this.recordingOptions = __assign({ maskAllInputs: maskAllInputs, blockClass: blockClass, ignoreClass: ignoreClass, maskTextClass: maskTextClass, blockSelector: blockSelector }, recordingOptions);
        var usingDeprecatedReplaysSamplingRate = replaysSamplingRate !== undefined;
        var usingDeprecatedCaptureOnlyOnError = captureOnlyOnError !== undefined;
        this.options = {
            flushMinDelay: flushMinDelay,
            flushMaxDelay: flushMaxDelay,
            stickySession: stickySession,
            initialFlushDelay: initialFlushDelay,
            sessionSampleRate: usingDeprecatedReplaysSamplingRate
                ? replaysSamplingRate
                : sessionSampleRate,
            errorSampleRate: usingDeprecatedCaptureOnlyOnError
                ? 1.0
                : errorSampleRate,
            useCompression: useCompression,
            maskAllText: maskAllText,
            blockAllMedia: blockAllMedia,
        };
        // TODO(deprecated): Maintain backwards compatibility for alpha users
        if (usingDeprecatedCaptureOnlyOnError) {
            console.warn('[@sentry/replay]: The `captureOnlyOnError` option is deprecated! Please configure `errorSampleRate` instead.');
        }
        if (usingDeprecatedReplaysSamplingRate) {
            console.warn('[@sentry/replay]: The `replaysSamplingRate` option is deprecated! Please configure `sessionSampleRate` instead.');
        }
        if (this.options.maskAllText) {
            // `maskAllText` is a more user friendly option to configure
            // `maskTextSelector`. This means that all nodes will have their text
            // content masked.
            this.recordingOptions.maskTextSelector = '*';
        }
        if (this.options.blockAllMedia) {
            // `blockAllMedia` is a more user friendly option to configure blocking
            // embedded media elements
            this.recordingOptions.blockSelector = !this.recordingOptions.blockSelector
                ? MEDIA_SELECTORS
                : "".concat(this.recordingOptions.blockSelector, ",").concat(MEDIA_SELECTORS);
        }
        this.debouncedFlush = debounce(function () { return _this.flush(); }, this.options.flushMinDelay, {
            maxWait: this.options.flushMaxDelay,
        });
        if (isBrowser && _initialized) {
            var error = new Error('Multiple Sentry Session Replay instances are not supported');
            captureInternalException(error);
            throw error;
        }
        _initialized = true;
    }
    /**
     * We previously used to create a transaction in `setupOnce` and it would
     * potentially create a transaction before some native SDK integrations have run
     * and applied their own global event processor. An example is:
     * https://github.com/getsentry/sentry-javascript/blob/b47ceafbdac7f8b99093ce6023726ad4687edc48/packages/browser/src/integrations/useragent.ts
     *
     * So we call `this.setup` in next event loop as a workaround to wait for other
     * global event processors to finish. This is no longer needed, but keeping it
     * here to avoid any future issues.
     */
    Replay.prototype.setupOnce = function () {
        var _this = this;
        if (!isBrowser) {
            return;
        }
        // XXX: See method comments above
        window.setTimeout(function () { return _this.start(); });
    };
    /**
     * Initializes the plugin.
     *
     * Creates or loads a session, attaches listeners to varying events (DOM,
     * PerformanceObserver, Recording, Sentry SDK, etc)
     */
    Replay.prototype.start = function () {
        if (!isBrowser) {
            return;
        }
        this.setInitialState();
        this.loadSession({ expiry: SESSION_IDLE_DURATION });
        // If there is no session, then something bad has happened - can't continue
        if (!this.session) {
            captureInternalException(new Error('Invalid session'));
            return;
        }
        if (!this.session.sampled) {
            // If session was not sampled, then we do not initialize the integration at all.
            return;
        }
        // Modify recording options to checkoutEveryNthSecond if
        // sampling for error replay. This is because we don't know
        // when an error will occur, so we need to keep a buffer of
        // replay events.
        if (this.session.sampled === 'error') {
            // Checkout every minute, meaning we only get up-to one minute of events before the error happens
            this.recordingOptions.checkoutEveryNms = 60000;
            this.waitForError = true;
        }
        // setup() is generally called on page load or manually - in both cases we
        // should treat it as an activity
        this.updateSessionActivity();
        this.eventBuffer = createEventBuffer({
            useCompression: Boolean(this.options.useCompression),
        });
        this.addListeners();
        this.startRecording();
        this.isEnabled = true;
    };
    /**
     * Start recording.
     *
     * Note that this will cause a new DOM checkout
     */
    Replay.prototype.startRecording = function () {
        try {
            this.stopRecording = record(__assign(__assign({}, this.recordingOptions), { emit: this.handleRecordingEmit }));
        }
        catch (err) {
            logger.error(err);
            captureInternalException(err);
        }
    };
    /**
     * Currently, this needs to be manually called (e.g. for tests). Sentry SDK
     * does not support a teardown
     */
    Replay.prototype.stop = function () {
        var _a, _b;
        if (!isBrowser) {
            return;
        }
        try {
            logger.log('Stopping Replays');
            this.isEnabled = false;
            this.removeListeners();
            (_a = this.stopRecording) === null || _a === void 0 ? void 0 : _a.call(this);
            (_b = this.eventBuffer) === null || _b === void 0 ? void 0 : _b.destroy();
            this.eventBuffer = null;
        }
        catch (err) {
            logger.error(err);
            captureInternalException(err);
        }
    };
    /**
     * Pause some replay functionality. See comments for `isPaused`.
     * This differs from stop as this only stops DOM recording, it is
     * not as thorough of a shutdown as `stop()`.
     */
    Replay.prototype.pause = function () {
        this.isPaused = true;
        try {
            if (this.stopRecording) {
                this.stopRecording();
                this.stopRecording = undefined;
            }
        }
        catch (err) {
            logger.error(err);
            captureInternalException(err);
        }
    };
    /**
     * Resumes recording, see notes for `pause().
     *
     * Note that calling `startRecording()` here will cause a
     * new DOM checkout.`
     */
    Replay.prototype.resume = function () {
        this.isPaused = false;
        this.startRecording();
    };
    Replay.prototype.clearSession = function () {
        try {
            deleteSession();
            this.session = undefined;
        }
        catch (err) {
            logger.error(err);
            captureInternalException(err);
        }
    };
    /**
     * Loads a session from storage, or creates a new one if it does not exist or
     * is expired.
     */
    Replay.prototype.loadSession = function (_a) {
        var _b, _c;
        var expiry = _a.expiry;
        var _d = getSession({
            expiry: expiry,
            stickySession: Boolean(this.options.stickySession),
            currentSession: this.session,
            sessionSampleRate: this.options.sessionSampleRate,
            errorSampleRate: this.options.errorSampleRate,
        }), type = _d.type, session = _d.session;
        // If session was newly created (i.e. was not loaded from storage), then
        // enable flag to create the root replay
        if (type === 'new') {
            this.setInitialState();
        }
        if (session.id !== ((_b = this.session) === null || _b === void 0 ? void 0 : _b.id)) {
            session.previousSessionId = (_c = this.session) === null || _c === void 0 ? void 0 : _c.id;
        }
        this.session = session;
    };
    /**
     * Capture some initial state that can change throughout the lifespan of the
     * replay. This is required because otherwise they would be captured at the
     * first flush.
     */
    Replay.prototype.setInitialState = function () {
        var urlPath = "".concat(window.location.pathname).concat(window.location.hash).concat(window.location.search);
        var url = "".concat(window.location.origin).concat(urlPath);
        this.performanceEvents = [];
        // Reset context as well
        this.clearContext();
        this.context.initialUrl = url;
        this.context.initialTimestamp = new Date().getTime();
        this.context.urls.push(url);
    };
    /**
     * Adds listeners to record events for the replay
     */
    Replay.prototype.addListeners = function () {
        var _this = this;
        try {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
            window.addEventListener('blur', this.handleWindowBlur);
            window.addEventListener('focus', this.handleWindowFocus);
            // There is no way to remove these listeners, so ensure they are only added once
            if (!this.hasInitializedCoreListeners) {
                // Listeners from core SDK //
                var scope = getCurrentHub().getScope();
                scope === null || scope === void 0 ? void 0 : scope.addScopeListener(this.handleCoreBreadcrumbListener('scope'));
                addInstrumentationHandler('dom', this.handleCoreBreadcrumbListener('dom'));
                addInstrumentationHandler('fetch', this.handleCoreSpanListener('fetch'));
                addInstrumentationHandler('xhr', this.handleCoreSpanListener('xhr'));
                addInstrumentationHandler('history', this.handleCoreSpanListener('history'));
                // Tag all (non replay) events that get sent to Sentry with the current
                // replay ID so that we can reference them later in the UI
                addGlobalEventProcessor(this.handleGlobalEvent);
                this.hasInitializedCoreListeners = true;
            }
        }
        catch (err) {
            logger.error(err);
            captureInternalException(err);
        }
        // PerformanceObserver //
        if (!('PerformanceObserver' in window)) {
            return;
        }
        this.performanceObserver = new PerformanceObserver(this.handlePerformanceObserver);
        // Observe almost everything for now (no mark/measure)
        [
            'element',
            'event',
            'first-input',
            'largest-contentful-paint',
            'layout-shift',
            'longtask',
            'navigation',
            'paint',
            'resource',
        ].forEach(function (type) {
            var _a;
            try {
                (_a = _this.performanceObserver) === null || _a === void 0 ? void 0 : _a.observe({
                    type: type,
                    buffered: true,
                });
            }
            catch (_b) {
                // This can throw if an entry type is not supported in the browser.
                // Ignore these errors.
            }
        });
    };
    /**
     * Cleans up listeners that were created in `addListeners`
     */
    Replay.prototype.removeListeners = function () {
        try {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
            window.removeEventListener('blur', this.handleWindowBlur);
            window.removeEventListener('focus', this.handleWindowFocus);
            if (this.performanceObserver) {
                this.performanceObserver.disconnect();
                this.performanceObserver = null;
            }
        }
        catch (err) {
            logger.error(err);
            captureInternalException(err);
        }
    };
    /**
     * We want to batch uploads of replay events. Save events only if
     * `<flushMinDelay>` milliseconds have elapsed since the last event
     * *OR* if `<flushMaxDelay>` milliseconds have elapsed.
     *
     * Accepts a callback to perform side-effects and returns true to stop batch
     * processing and hand back control to caller.
     */
    Replay.prototype.addUpdate = function (cb) {
        // We need to always run `cb` (e.g. in the case of `this.waitForError == true`)
        var cbResult = cb === null || cb === void 0 ? void 0 : cb();
        // If this option is turned on then we will only want to call `flush`
        // explicitly
        if (this.waitForError) {
            return;
        }
        // If callback is true, we do not want to continue with flushing -- the
        // caller will need to handle it.
        if (cbResult === true) {
            return;
        }
        // addUpdate is called quite frequently - use debouncedFlush so that it
        // respects the flush delays and does not flush immediately
        this.debouncedFlush();
    };
    /**
     * Tasks to run when we consider a page to be hidden (via blurring and/or visibility)
     */
    Replay.prototype.doChangeToBackgroundTasks = function (breadcrumb) {
        if (!this.session) {
            return;
        }
        var expired = isSessionExpired(this.session, VISIBILITY_CHANGE_TIMEOUT);
        if (breadcrumb && !expired) {
            this.createCustomBreadcrumb(breadcrumb);
        }
        // Send replay when the page/tab becomes hidden. There is no reason to send
        // replay if it becomes visible, since no actions we care about were done
        // while it was hidden
        this.conditionalFlush();
    };
    /**
     * Tasks to run when we consider a page to be visible (via focus and/or visibility)
     */
    Replay.prototype.doChangeToForegroundTasks = function (breadcrumb) {
        if (!this.session) {
            return;
        }
        var isSessionActive = this.checkAndHandleExpiredSession({
            expiry: VISIBILITY_CHANGE_TIMEOUT,
        });
        if (!isSessionActive) {
            // If the user has come back to the page within VISIBILITY_CHANGE_TIMEOUT
            // ms, we will re-use the existing session, otherwise create a new
            // session
            logger.log('Document has become active, but session has expired');
            return;
        }
        if (breadcrumb) {
            this.createCustomBreadcrumb(breadcrumb);
        }
    };
    /**
     * Trigger rrweb to take a full snapshot which will cause this plugin to
     * create a new Replay event.
     */
    Replay.prototype.triggerFullSnapshot = function () {
        logger.log('Taking full rrweb snapshot');
        record.takeFullSnapshot(true);
    };
    /**
     * Add an event to the event buffer
     */
    Replay.prototype.addEvent = function (event, isCheckout) {
        var _a;
        if (!this.eventBuffer) {
            // This implies that `isEnabled` is false
            return;
        }
        if (this.isPaused) {
            // Do not add to event buffer when recording is paused
            return;
        }
        // TODO: sadness -- we will want to normalize timestamps to be in ms -
        // requires coordination with frontend
        var isMs = event.timestamp > 9999999999;
        var timestampInMs = isMs ? event.timestamp : event.timestamp * 1000;
        // Throw out events that happen more than 5 minutes ago. This can happen if
        // page has been left open and idle for a long period of time and user
        // comes back to trigger a new session. The performance entries rely on
        // `performance.timeOrigin`, which is when the page first opened.
        if (timestampInMs + SESSION_IDLE_DURATION < new Date().getTime()) {
            return;
        }
        // Only record earliest event if a new session was created, otherwise it
        // shouldn't be relevant
        if (((_a = this.session) === null || _a === void 0 ? void 0 : _a.segmentId) === 0 &&
            (!this.context.earliestEvent ||
                timestampInMs < this.context.earliestEvent)) {
            this.context.earliestEvent = timestampInMs;
        }
        this.eventBuffer.addEvent(event, isCheckout);
    };
    /**
     * Update user activity (across session lifespans)
     */
    Replay.prototype.updateUserActivity = function (lastActivity) {
        if (lastActivity === void 0) { lastActivity = new Date().getTime(); }
        this.lastActivity = lastActivity;
    };
    /**
     * Updates the session's last activity timestamp
     */
    Replay.prototype.updateSessionActivity = function (lastActivity) {
        if (lastActivity === void 0) { lastActivity = new Date().getTime(); }
        if (this.session) {
            this.session.lastActivity = lastActivity;
        }
    };
    /**
     * Updates the user activity timestamp and resumes recording. This should be
     * called in an event handler for a user action that we consider as the user
     * being "active" (e.g. a mouse click).
     */
    Replay.prototype.triggerUserActivity = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                this.updateUserActivity();
                // This case means that recording was once stopped due to inactivity.
                // Ensure that recording is resumed.
                if (!this.stopRecording) {
                    // Create a new session, otherwise when the user action is flushed, it
                    // will get rejected due to an expired session.
                    this.loadSession({ expiry: SESSION_IDLE_DURATION });
                    // Note: This will cause a new DOM checkout
                    this.resume();
                    return [2 /*return*/];
                }
                // Otherwise... recording was never suspended, continue as normalish
                this.checkAndHandleExpiredSession();
                this.updateSessionActivity();
                return [2 /*return*/];
            });
        });
    };
    /**
     * Helper to create (and buffer) a replay breadcrumb from a core SDK breadcrumb
     */
    Replay.prototype.createCustomBreadcrumb = function (breadcrumb) {
        var _this = this;
        this.addUpdate(function () {
            _this.addEvent({
                type: EventType.Custom,
                timestamp: breadcrumb.timestamp || 0,
                data: {
                    tag: 'breadcrumb',
                    payload: breadcrumb,
                },
            });
        });
    };
    /**
     * Create a "span" for each performance entry. The parent transaction is `this.replayEvent`.
     */
    Replay.prototype.createPerformanceSpans = function (entries) {
        var _this = this;
        return Promise.all(entries.map(function (_a) {
            var type = _a.type, start = _a.start, end = _a.end, name = _a.name, data = _a.data;
            return _this.addEvent({
                type: EventType.Custom,
                timestamp: start,
                data: {
                    tag: 'performanceSpan',
                    payload: {
                        op: type,
                        description: name,
                        startTimestamp: start,
                        endTimestamp: end,
                        data: data,
                    },
                },
            });
        }));
    };
    /**
     * Observed performance events are added to `this.performanceEvents`. These
     * are included in the replay event before it is finished and sent to Sentry.
     */
    Replay.prototype.addPerformanceEntries = function () {
        // Copy and reset entries before processing
        var entries = __spreadArray([], this.performanceEvents, true);
        this.performanceEvents = [];
        return this.createPerformanceSpans(createPerformanceEntries(entries));
    };
    /**
     * Create a "span" for the total amount of memory being used by JS objects
     * (including v8 internal objects).
     */
    Replay.prototype.addMemoryEntry = function () {
        // window.performance.memory is a non-standard API and doesn't work on all browsers
        // so we check before creating the event.
        if (!('memory' in window.performance)) {
            return;
        }
        return this.createPerformanceSpans([
            // @ts-expect-error memory doesn't exist on type Performance as the API is non-standard (we check that it exists above)
            createMemoryEntry(window.performance.memory),
        ]);
    };
    /**
     * Checks if recording should be stopped due to user inactivity. Otherwise
     * check if session is expired and create a new session if so. Triggers a new
     * full snapshot on new session.
     *
     * Returns true if session is not expired, false otherwise.
     */
    Replay.prototype.checkAndHandleExpiredSession = function (_a) {
        var _b, _c;
        var _d = _a === void 0 ? {} : _a, _e = _d.expiry, expiry = _e === void 0 ? SESSION_IDLE_DURATION : _e;
        var oldSessionId = (_b = this.session) === null || _b === void 0 ? void 0 : _b.id;
        // Prevent starting a new session if the last user activity is older than
        // MAX_SESSION_LIFE. Otherwise non-user activity can trigger a new
        // session+recording. This creates noisy replays that do not have much
        // content in them.
        if (this.lastActivity && isExpired(this.lastActivity, MAX_SESSION_LIFE)) {
            // Pause recording
            this.pause();
            return;
        }
        // --- There is recent user activity --- //
        // This will create a new session if expired, based on expiry length
        this.loadSession({ expiry: expiry });
        // Session was expired if session ids do not match
        var expired = oldSessionId !== ((_c = this.session) === null || _c === void 0 ? void 0 : _c.id);
        if (!expired) {
            return true;
        }
        // Session is expired, trigger a full snapshot (which will create a new session)
        this.triggerFullSnapshot();
        return false;
    };
    /**
     * Only flush if `this.waitForError` is false.
     */
    Replay.prototype.conditionalFlush = function () {
        if (this.waitForError) {
            return;
        }
        this.flushImmediate();
    };
    /**
     * Clear context
     */
    Replay.prototype.clearContext = function () {
        // XXX: `initialTimestamp` and `initialUrl` do not get cleared
        this.context.errorIds.clear();
        this.context.traceIds.clear();
        this.context.urls = [];
        this.context.earliestEvent = null;
    };
    /**
     * Return and clear context
     */
    Replay.prototype.popEventContext = function () {
        if (this.context.earliestEvent &&
            this.context.earliestEvent < this.context.initialTimestamp) {
            this.context.initialTimestamp = this.context.earliestEvent;
        }
        var context = {
            initialTimestamp: this.context.initialTimestamp,
            initialUrl: this.context.initialUrl,
            errorIds: Array.from(this.context.errorIds).filter(Boolean),
            traceIds: Array.from(this.context.traceIds).filter(Boolean),
            urls: this.context.urls,
        };
        this.clearContext();
        return context;
    };
    /**
     * Flushes replay event buffer to Sentry.
     *
     * Performance events are only added right before flushing - this is
     * due to the buffered performance observer events.
     *
     * Should never be called directly, only by `flush`
     */
    Replay.prototype.runFlush = function () {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var recordingData, replayId, eventContext, segmentId, err_2;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!this.session) {
                            console.error(new Error('[Sentry]: No transaction, no replay'));
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, this.addPerformanceEntries()];
                    case 1:
                        _b.sent();
                        if (!((_a = this.eventBuffer) === null || _a === void 0 ? void 0 : _a.length)) {
                            return [2 /*return*/];
                        }
                        // Only attach memory event if eventBuffer is not empty
                        return [4 /*yield*/, this.addMemoryEntry()];
                    case 2:
                        // Only attach memory event if eventBuffer is not empty
                        _b.sent();
                        _b.label = 3;
                    case 3:
                        _b.trys.push([3, 6, , 7]);
                        return [4 /*yield*/, this.eventBuffer.finish()];
                    case 4:
                        recordingData = _b.sent();
                        replayId = this.session.id;
                        eventContext = this.popEventContext();
                        segmentId = this.session.segmentId++;
                        return [4 /*yield*/, this.sendReplay({
                                replayId: replayId,
                                events: recordingData,
                                segmentId: segmentId,
                                includeReplayStartTimestamp: segmentId === 0,
                                eventContext: eventContext,
                            })];
                    case 5:
                        _b.sent();
                        return [3 /*break*/, 7];
                    case 6:
                        err_2 = _b.sent();
                        captureInternalException(err_2);
                        console.error(err_2);
                        return [3 /*break*/, 7];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    /**
     *
     * Always flush via `debouncedFlush` so that we do not have flushes triggered
     * from calling both `flush` and `debouncedFlush`. Otherwise, there could be
     * cases of mulitple flushes happening closely together.
     */
    Replay.prototype.flushImmediate = function () {
        this.debouncedFlush();
        // `.flush` is provided by lodash.debounce
        return this.debouncedFlush.flush();
    };
    /**
     * Send replay attachment using `fetch()`
     */
    Replay.prototype.sendReplayRequest = function (_a) {
        var _b;
        var events = _a.events, event_id = _a.replayId, segment_id = _a.segmentId, includeReplayStartTimestamp = _a.includeReplayStartTimestamp, eventContext = _a.eventContext;
        return __awaiter(this, void 0, void 0, function () {
            var payloadWithSequence, urls, errorIds, traceIds, initialTimestamp, currentTimestamp, sdkInfo, replayEvent, envelope, client;
            var _this = this;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        payloadWithSequence = createPayload({
                            events: events,
                            headers: {
                                segment_id: segment_id,
                            },
                        });
                        urls = eventContext.urls, errorIds = eventContext.errorIds, traceIds = eventContext.traceIds, initialTimestamp = eventContext.initialTimestamp;
                        currentTimestamp = new Date().getTime();
                        sdkInfo = {
                            name: 'sentry.javascript.integration.replay',
                            version: "0.6.14",
                        };
                        return [4 /*yield*/, new Promise(function (resolve) {
                                var _a;
                                (_a = getCurrentHub()) === null || _a === void 0 ? void 0 : _a._withClient(function (client, scope) { return __awaiter(_this, void 0, void 0, function () {
                                    var preparedEvent, session;
                                    var _a;
                                    return __generator(this, function (_b) {
                                        switch (_b.label) {
                                            case 0: return [4 /*yield*/, client._prepareEvent(__assign(__assign({ type: REPLAY_EVENT_NAME }, (includeReplayStartTimestamp
                                                    ? { replay_start_timestamp: initialTimestamp / 1000 }
                                                    : {})), { timestamp: currentTimestamp / 1000, error_ids: errorIds, trace_ids: traceIds, urls: urls, replay_id: event_id, segment_id: segment_id }), { event_id: event_id }, scope)];
                                            case 1:
                                                preparedEvent = _b.sent();
                                                session = scope && scope.getSession();
                                                if (session) {
                                                    client._updateSessionFromEvent(session, preparedEvent);
                                                }
                                                preparedEvent.sdk = __assign(__assign({}, preparedEvent.sdk), sdkInfo);
                                                preparedEvent.tags = __assign(__assign({}, preparedEvent.tags), { sessionSampleRate: this.options.sessionSampleRate, errorSampleRate: this.options.errorSampleRate, replayType: (_a = this.session) === null || _a === void 0 ? void 0 : _a.sampled });
                                                resolve(preparedEvent);
                                                return [2 /*return*/];
                                        }
                                    });
                                }); });
                            })];
                    case 1:
                        replayEvent = _c.sent();
                        envelope = createEnvelope({
                            event_id: event_id,
                            sent_at: new Date().toISOString(),
                            sdk: sdkInfo,
                        }, [
                            // @ts-expect-error New types
                            [{ type: 'replay_event' }, replayEvent],
                            [
                                {
                                    // @ts-expect-error setting envelope
                                    type: 'replay_recording',
                                    length: payloadWithSequence.length,
                                },
                                // @ts-expect-error: Type 'string' is not assignable to type 'ClientReport'.ts(2322)
                                payloadWithSequence,
                            ],
                        ]);
                        client = getCurrentHub().getClient();
                        try {
                            return [2 /*return*/, (_b = client === null || client === void 0 ? void 0 : client.getTransport()) === null || _b === void 0 ? void 0 : _b.send(envelope)];
                        }
                        catch (_d) {
                            throw new Error(UNABLE_TO_SEND_REPLAY);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    Replay.prototype.resetRetries = function () {
        this.retryCount = 0;
        this.retryInterval = BASE_RETRY_INTERVAL;
    };
    /**
     * Finalize and send the current replay event to Sentry
     */
    Replay.prototype.sendReplay = function (_a) {
        var replayId = _a.replayId, events = _a.events, segmentId = _a.segmentId, includeReplayStartTimestamp = _a.includeReplayStartTimestamp, eventContext = _a.eventContext;
        return __awaiter(this, void 0, void 0, function () {
            var ex_1;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        // short circuit if there's no events to upload (this shouldn't happen as runFlush makes this check)
                        if (!events.length) {
                            return [2 /*return*/];
                        }
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 5]);
                        return [4 /*yield*/, this.sendReplayRequest({
                                events: events,
                                replayId: replayId,
                                segmentId: segmentId,
                                includeReplayStartTimestamp: includeReplayStartTimestamp,
                                eventContext: eventContext,
                            })];
                    case 2:
                        _b.sent();
                        this.resetRetries();
                        return [2 /*return*/, true];
                    case 3:
                        ex_1 = _b.sent();
                        console.error(ex_1);
                        // Capture error for every failed replay
                        setContext('Replays', {
                            retryCount: this.retryCount,
                        });
                        captureInternalException(ex_1);
                        // If an error happened here, it's likely that uploading the attachment
                        // failed, we'll can retry with the same events payload
                        if (this.retryCount >= MAX_RETRY_COUNT) {
                            throw new Error("".concat(UNABLE_TO_SEND_REPLAY, " - max retries exceeded"));
                        }
                        this.retryCount = this.retryCount + 1;
                        // will retry in intervals of 5, 10, 30
                        this.retryInterval = this.retryCount * this.retryInterval;
                        return [4 /*yield*/, new Promise(function (resolve, reject) {
                                setTimeout(function () { return __awaiter(_this, void 0, void 0, function () {
                                    var err_3;
                                    return __generator(this, function (_a) {
                                        switch (_a.label) {
                                            case 0:
                                                _a.trys.push([0, 2, , 3]);
                                                return [4 /*yield*/, this.sendReplay({
                                                        replayId: replayId,
                                                        events: events,
                                                        segmentId: segmentId,
                                                        includeReplayStartTimestamp: includeReplayStartTimestamp,
                                                        eventContext: eventContext,
                                                    })];
                                            case 1:
                                                _a.sent();
                                                resolve(true);
                                                return [3 /*break*/, 3];
                                            case 2:
                                                err_3 = _a.sent();
                                                reject(err_3);
                                                return [3 /*break*/, 3];
                                            case 3: return [2 /*return*/];
                                        }
                                    });
                                }); }, _this.retryInterval);
                            })];
                    case 4: return [2 /*return*/, _b.sent()];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @inheritDoc
     */
    Replay.id = 'Replay';
    return Replay;
}());

export { Replay };
